import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase, getRecentFetches, rawRestProbe, SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/supabase'
import { computeNextDueDate } from '@/lib/utils'
import { retryWrite, retryTransient, flushStalePendingWrites, flushStalePendingWritesRaw, enqueue, pendingWriteCount, loadQueue, cancelQueuedInserts, isEntityPending } from '@/lib/writeQueue'
import { generateImageThumbFromUrl } from '@/lib/mediaThumbnails'
import { r2Enabled, r2PublicUrl, r2KeyFromUrl, r2Presign } from '@/lib/r2'

// Per-entity in-flight tracking — prevents blocking OTHER users' realtime updates
// while a local write for a SPECIFIC task is in-flight
const _inFlightEntityIds = new Set()

// Reconciliation guard. An entity is "protected" while a local write for it is
// unconfirmed — either in the in-memory in-flight set (the brief optimistic window
// before the durable enqueue) OR sitting in the durable write queue (survives
// timeouts, background retries, and reloads). A protected entity must never be
// overwritten by an incoming server row, so a stale echo of our own earlier write
// can't revert a fresh local edit.
function _isProtected(table, id) {
  if (table === 'tasks' && _inFlightEntityIds.has(id)) return true
  return isEntityPending(table, id)
}

// Whether an incoming row should overwrite local state: never if protected,
// otherwise only if it is the same-age-or-newer than what we hold (monotonic
// updated_at guard). Missing versions fall back to apply (last-writer-wins) — this
// is what keeps it safe for sections/projects/goals before their updated_at column
// exists (updatedAt is null there until the migration runs → apply as today).
function _shouldApply(table, local, incoming) {
  if (!local) return true
  if (_isProtected(table, incoming.id)) return false
  const lu = local.updatedAt, iu = incoming.updatedAt
  if (!lu || !iu) return true
  return new Date(iu).getTime() >= new Date(lu).getTime()
}

// Debounce timers for updateTask — prevents race conditions when the user
// makes rapid changes (e.g. typing). Only one network write fires per task
// per 500ms window, and it always uses the latest data from the store.
const _taskWriteTimers = {}

// Guards lazy thumbnail backfill: three cards may render the same task at
// once, so without this we'd fire the same generateImageThumbFromUrl + upload
// pipeline three times for a single attachment.
const _thumbBackfillInFlight = new Set()

// Active liveness probe. Runs while the persistent write queue has entries
// older than 15s — neither navigator.onLine nor Phoenix CHANNEL_ERROR fire on
// flaky connections (browser still shows online, WS stays in zombie 'joined'
// state), so this is the only reliable trigger for auto-flush on recovery.
let _pingPollTimer = null
// Last time we forcibly recreated realtime channels in response to a write
// timeout. Rate-limit: don't churn the WS more than once per 30s, even if
// many writes time out in a burst.
let _lastRealtimeKickAt = 0
// Zombie-WS recovery: independent rate-limit + consecutive-evidence counter for the
// "socket joined but silent" case (REST alive, no channel actually joined). Kept
// separate from _lastRealtimeKickAt so a write-timeout kick and a zombie kick don't
// block each other.
let _lastZombieKickAt = 0
let _zombieTicks = 0
// Auth-lock wedge recovery: the in-tab GoTrueClient deadlock where the client REST path hangs
// while the websocket stays joined. The trigger is the gotrue auth lock never releasing (a
// lock-held op that never settles) — every getSession/refreshSession then blocks, so once the
// cached JWT expires each REST call 401s and stalls on the wedged refreshSession retry. Detected
// when writes are stuck, online, a channel is joined, the client-routed ping fails, but a
// lock-free probe (no client, no lock) succeeds. The poll's 10s cadence + the queue's
// single-flight guard pace the raw escalation flush; count failed cycles to decide when to reload.
let _wedgeRecoveryAttempts = 0
let _lastWedgeReloadAt = 0
// Re-entrancy guard for the wedge handler. The poll fires every 10s but a raw drain can take far
// longer; without this, overlapping ticks would inflate _wedgeRecoveryAttempts and trip the reload
// before the first drain even finishes.
let _wedgeBusy = false
// Count of in-flight attachment uploads (XHR). The guarded wedge reload must never interrupt one.
let _activeUploads = 0
// Number of consecutive raw-flush cycles that fail to drain the queue before we escalate to a
// guarded reload. Each cycle is one poll tick (~10s), so 3 ≈ 30s of raw attempts first.
const WEDGE_RAW_FLUSH_MAX_CYCLES = 3
// Writes must be stuck this long (online, channel joined, client ping failing) before we suspect
// a wedge — past one full client-flush budget so we don't false-positive on a merely slow link.
const WEDGE_THRESHOLD_MS = 45000

// Self-initiated project leaves — prevents the realtime DELETE handler from showing
// the "you were kicked" screen when the user leaves a project themselves
const _selfLeftProjects = new Set()

// Whether an attachment should be persisted to the DB. A saved attachment always
// carries a url (inspo links, uploaded files), so anything with a url is kept no
// matter what flags it has. Only genuinely empty in-flight placeholders (url-less +
// uploading/loading) are dropped. This makes it impossible for a stale
// loading/uploading flag to silently delete a saved attachment — the bug that wiped
// inspo links Apr 10–13.
export const isPersistableAttachment = (a) => !!a.url || !(a.uploading || a.loading)

// Human-readable labels for the Postgres error codes the writeQueue treats as fatal.
// Mirrors FATAL_PG_CODES in writeQueue.js. Surfaced in the user-visible toast so a
// "Failed to delete …" message lands as "(42501 — permission denied)" instead of a
// dead-end string. Any code not listed here falls through to the raw code in the toast.
const FATAL_PG_LABELS = {
  '42501': 'permission denied',
  '23503': 'still referenced',
  '23502': 'required field missing',
  '42703': 'unknown column',
  '42804': 'type mismatch',
  '42P01': 'unknown table',
}

// ── DB → app mappers ──────────────────────────────────────────────────────────
function mapTask(r) {
  return {
    id: r.id,
    title: r.title || '',
    description: r.description || '',
    projectId: r.project_id,
    sectionId: r.section_id || null,
    assigneeId: r.assignee_id || null,
    dueDate: r.due_date || null,
    startDate: r.start_date || null,
    priority: r.priority || 'none',
    completed: r.completed || false,
    completedAt: r.completed_at || null,
    tags: r.tags || [],
    parentId: r.parent_id || null,
    subtasks: r.subtasks || [],
    comments: r.comments || [],
    attachments: (r.attachments || []).map((a) => {
      let att = (a.uploading || a.loading) ? { ...a, uploading: false, loading: false } : a
      // Derive isVideo from URL for attachments saved before the isVideo flag was added
      if (!att.isVideo && !att.isImage && att.url && /\.(mp4|webm|mov|avi|m4v|mkv)(\?|#|$)/i.test(att.url)) {
        att = { ...att, isVideo: true }
      }
      return att
    }),
    recurrence: r.recurrence || null,
    createdAt: r.created_at,
    createdBy: r.created_by || null,
    deliverableType: r.deliverable_type || null,
    contentSizes: (() => { const v = r.content_size; if (!v) return []; if (Array.isArray(v)) return v; try { const p = JSON.parse(v); return Array.isArray(p) ? p : [v]; } catch { return [v]; } })(),
    position: r.position ?? 0,
    updatedAt: r.updated_at || null,
  }
}

// Patch-mapper for a queued task UPDATE snapshot (buildFullSnapshot output: DB-shaped,
// snake_case, mutable fields only — NO id/project_id/created_at). Converts only the
// keys actually present into camelCase store fields so the result can be merged onto
// an existing mapped task without clobbering id/projectId/createdAt — which a full
// mapTask() would overwrite with falsy defaults. Used by the loadUserData overlay so a
// refresh reflects a queued-but-unflushed edit instead of the stale server value.
function mapTaskPatch(d) {
  const p = {}
  if (!d || typeof d !== 'object') return p
  if ('title' in d) p.title = d.title || ''
  if ('description' in d) p.description = d.description || ''
  if ('section_id' in d) p.sectionId = d.section_id || null
  if ('assignee_id' in d) p.assigneeId = d.assignee_id || null
  if ('due_date' in d) p.dueDate = d.due_date || null
  if ('start_date' in d) p.startDate = d.start_date || null
  if ('priority' in d) p.priority = d.priority || 'none'
  if ('completed' in d) p.completed = d.completed || false
  if ('completed_at' in d) p.completedAt = d.completed_at || null
  if ('tags' in d) p.tags = d.tags || []
  if ('parent_id' in d) p.parentId = d.parent_id || null
  if ('subtasks' in d) p.subtasks = d.subtasks || []
  if ('comments' in d) p.comments = d.comments || []
  if ('attachments' in d) {
    p.attachments = (d.attachments || []).map((a) => {
      let att = (a.uploading || a.loading) ? { ...a, uploading: false, loading: false } : a
      if (!att.isVideo && !att.isImage && att.url && /\.(mp4|webm|mov|avi|m4v|mkv)(\?|#|$)/i.test(att.url)) att = { ...att, isVideo: true }
      return att
    })
  }
  if ('recurrence' in d) p.recurrence = d.recurrence || null
  if ('deliverable_type' in d) p.deliverableType = d.deliverable_type || null
  if ('content_size' in d) {
    const v = d.content_size
    p.contentSizes = !v ? [] : Array.isArray(v) ? v : (() => { try { const x = JSON.parse(v); return Array.isArray(x) ? x : [v] } catch { return [v] } })()
  }
  if ('position' in d) p.position = d.position ?? 0
  if ('updated_at' in d) p.updatedAt = d.updated_at || null
  return p
}

function mapProject(r) {
  return {
    id: r.id,
    name: r.name || '',
    color: r.color || '#e63946',
    icon: r.icon || '◈',
    description: r.description || '',
    privacy: r.privacy || 'private',
    completed: r.completed || false,
    ownerId: r.owner_id,
    parentId: r.parent_id || null,
    isWorkspace: r.is_workspace || false,
    views: r.views || [],
    brandKit: r.brand_kit || { colors: [], fonts: [], logos: [] },
    embeds: r.embeds || [],
    updatedAt: r.updated_at || null,
  }
}

function mapSection(r) {
  // Prefer DB type column → localStorage fallback → name convention
  const dbType = r.type || null
  const sectionTypes = (() => { try { return JSON.parse(localStorage.getItem('prjct-section-types') || '{}') } catch { return {} } })()
  const localType = sectionTypes[r.id] || null
  const nameType = (
    /^approval$/i.test((r.name || '').trim()) ? 'approval' :
    /^complete[d]?$/i.test((r.name || '').trim()) ? 'complete' :
    /^misc$/i.test((r.name || '').trim()) ? 'misc' :
    null
  )
  return { id: r.id, projectId: r.project_id, name: r.name, order: r.order || 0, type: dbType || localType || nameType, approvers: r.approvers || [], deliverableType: r.deliverable_type || null, deliverableQuota: r.deliverable_quota || null, updatedAt: r.updated_at || null }
}

function mapGoal(r) {
  return {
    id: r.id,
    title: r.title,
    description: r.description || '',
    progress: r.progress || 0,
    status: r.status || 'on_track',
    dueDate: r.due_date || null,
    createdAt: r.created_at,
    updatedAt: r.updated_at || null,
  }
}

// ── Mock mode data (when Supabase not configured) ────────────────────────────
const MOCK_USER = { id: 'u1', name: 'Porter Gerlach', email: 'porter@blanq.co', avatar: null }
const MOCK_WS = { name: 'BLANQ' }
const MOCK_PROJECTS = [
  { id: 'p1', name: 'ZORTS // BLSSM', color: '#e63946', icon: '🌸', description: '', privacy: 'private', completed: false, ownerId: 'u1' },
  { id: 'p2', name: 'GEN', color: '#a78bfa', icon: '✦', description: '', privacy: 'private', completed: false, ownerId: 'u1' },
  { id: 'p3', name: 'RIVVIA', color: '#34d399', icon: '◈', description: '', privacy: 'private', completed: false, ownerId: 'u1' },
]
const MOCK_SECTIONS = [
  { id: 's1', projectId: 'p1', name: 'TO DO', order: 0 },
  { id: 's2', projectId: 'p1', name: 'IN PROGRESS', order: 1 },
  { id: 's3', projectId: 'p1', name: 'DONE', order: 2 },
  { id: 's4', projectId: 'p2', name: 'GRAPHICS', order: 0 },
  { id: 's5', projectId: 'p2', name: 'COPY', order: 1 },
]
function mockTasks(uid) {
  const today = new Date().toISOString().split('T')[0]
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0]
  return [
    { id: 't1', title: 'SPPLY APRIL', sectionId: 's1', projectId: 'p1', assigneeId: uid, dueDate: today, priority: 'high', description: '', subtasks: [], tags: [], completed: false, createdAt: new Date().toISOString() },
    { id: 't2', title: 'OUT OF STOCK APRIL', sectionId: 's1', projectId: 'p1', assigneeId: uid, dueDate: today, priority: 'medium', description: '', subtasks: [], tags: [], completed: false, createdAt: new Date().toISOString() },
    { id: 't3', title: 'UPDATE GRAPHICS', sectionId: 's4', projectId: 'p2', assigneeId: uid, dueDate: tomorrow, priority: 'medium', description: '', subtasks: [], tags: [], completed: false, createdAt: new Date().toISOString() },
  ]
}

export const useAppStore = create(
  persist(
    (set, get) => ({
      // ── Auth ─────────────────────────────────────────────────────────────────
      currentUser: null,
      isAuthenticated: false,
      workspace: null,
      _authReady: false,
      // Cached access token + expiry (ms since epoch). Populated by initAuth + onAuthStateChange.
      // Lets uploadAttachment authenticate without calling supabase.auth.getSession(), which can
      // hang for seconds on Safari (Web Locks contention / autoRefreshToken throttled in bg tabs).
      _authToken: null,
      _authExpiresAt: 0,
      // ── Data ─────────────────────────────────────────────────────────────────
      projects: [],
      sections: [],
      tasks: [],
      goals: [],
      _membersCache: {}, // projectId → members array (avoids N+1 DB calls)
      _inFlight: 0,     // count of in-flight DB writes — drives sync health indicator
      _pendingSyncIds: [], // task IDs whose INSERT hasn't been confirmed by the DB yet
      _lastSyncAt: 0,   // timestamp of last successful loadUserData — drives stale banner

      // ── UI State ─────────────────────────────────────────────────────────────
      _projectOrder: (() => { try { return JSON.parse(localStorage.getItem('prjct-project-order') || '{}') } catch { return {} } })(),
      _viewPrefs: (() => { try { return JSON.parse(localStorage.getItem('prjct-view-prefs') || '{}') } catch { return {} } })(),
      activeProjectId: null,
      activeView: 'board',
      selectedTaskId: null,
      sidebarCollapsed: false,
      theme: 'dark',
      pendingCompletion: null, // { taskId, origin } — awaiting user's destination choice
      completionPreference: (() => { try { return localStorage.getItem('prjct-completion-pref') || null } catch { return null } })(),

      // ── Kicked-from-project screen ───────────────────────────────────────
      kickedFromProject: null, // { id, name } — set when current user is removed by someone else

      // ── Notifications ────────────────────────────────────────────────────
      notifications: [],
      notifPrefs: { assignments: true, mentions: true, completions: false, reminders: true, comments: true, invites: false },
      setNotifPrefs: (prefs) => set((s) => ({ notifPrefs: { ...s.notifPrefs, ...prefs } })),
      addNotification: (notif) => set((s) => ({
        notifications: [{
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          read: false,
          ...notif,
        }, ...s.notifications].slice(0, 200),
      })),
      markNotificationRead: (id) => {
        set((s) => ({ notifications: s.notifications.map((n) => n.id === id ? { ...n, read: true } : n) }))
        // Notifications are low-stakes — use retryTransient so transient network errors don't
        // get swallowed silently the way the old fire-and-forget .then() did.
        if (supabase) retryTransient(
          () => supabase.from('notifications').update({ read: true }).eq('id', id),
          { onFail: (e) => console.warn('[notif markRead]', e?.message) }
        )
      },
      markAllNotificationsRead: () => {
        const ids = get().notifications.filter((n) => !n.read).map((n) => n.id)
        set((s) => ({ notifications: s.notifications.map((n) => ({ ...n, read: true })) }))
        if (supabase && ids.length) retryTransient(
          () => supabase.from('notifications').update({ read: true }).in('id', ids),
          { onFail: (e) => console.warn('[notif markAllRead]', e?.message) }
        )
      },
      dismissNotification: (id) => {
        set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) }))
        if (supabase) retryTransient(
          () => supabase.from('notifications').delete().eq('id', id),
          { onFail: (e) => console.warn('[notif dismiss]', e?.message) }
        )
      },
      dismissAllNotifications: () => {
        const ids = get().notifications.map((n) => n.id)
        set({ notifications: [] })
        if (supabase && ids.length) retryTransient(
          () => supabase.from('notifications').delete().in('id', ids),
          { onFail: (e) => console.warn('[notif dismissAll]', e?.message) }
        )
      },

      notifyMention: (mentionedUserId, { taskId, projectId, byName, taskTitle }) => {
        if (!supabase || !mentionedUserId) return
        retryTransient(
          () => supabase.from('notifications').insert({
            user_id: mentionedUserId,
            type: 'mention',
            title: `${byName} mentioned you`,
            body: taskTitle ? `In task: "${taskTitle}"` : 'You were mentioned',
            task_id: taskId || null,
            project_id: projectId || null,
          }),
          { onFail: (e) => console.warn('[notifyMention]', e?.message) }
        )
      },

      // Cross-user notification — writes to Supabase if targeting another user, local-only if self
      notifyUser: (userId, notif) => {
        if (!userId) return
        if (userId === get().currentUser?.id) {
          get().addNotification(notif)
          return
        }
        if (!supabase) return
        retryTransient(
          () => supabase.from('notifications').insert({
            user_id: userId,
            type: notif.type,
            title: notif.title,
            body: notif.body || null,
            task_id: notif.taskId || null,
            project_id: notif.projectId || null,
          }),
          { onFail: (e) => console.warn('[notifyUser]', e?.message) }
        )
      },

      // ── History ──────────────────────────────────────────────────────────────
      _history: [],
      _future: [],

      accentColor: '#e63946',

      // ── UI Actions ────────────────────────────────────────────────────────────
      setActiveProject: (id) => {
        if (!id) { set({ activeProjectId: null, selectedTaskId: null }); return }
        // Compute all derived values before any set() call
        const { _viewPrefs, projects, currentUser, sections, activeProjectId: prevProjectId, selectedTaskId: prevSelectedTaskId } = get()
        // Preserve selectedTaskId when re-activating the same project — e.g. ProjectPage's
        // mount effect re-calls setActiveProject after a deep link has already set both the
        // project and the task (inbox → task, home → task). Switching projects still clears.
        const projectChanged = prevProjectId !== id
        const project = projects.find((p) => p.id === id)
        const isWorkspace = project?.isWorkspace || projects.some((p) => p.parentId === id)
        const isClientBoard = !!project?.parentId
        const defaultView = isWorkspace ? 'board' : isClientBoard ? 'dashboard' : 'board'
        const activeView = _viewPrefs[id] || defaultView
        // Compute any missing system sections inline so we can batch into one set() call.
        // IMPORTANT: avoids multiple synchronous set() calls (which would each trigger a
        // useSyncExternalStore notification and may increment React's nestedUpdateCount).
        const isOwner = !isWorkspace && (project?.ownerId === currentUser?.id || project?.role === 'owner')
        const projectSections = sections.filter((s) => s.projectId === id)
        const newSections = []
        if (isOwner) {
          if (!projectSections.some((s) => s.type === 'approval')) {
            const secId = crypto.randomUUID()
            newSections.push({ id: secId, projectId: id, name: 'Approval', type: 'approval', order: projectSections.length, deliverableType: null, deliverableQuota: null })
          }
          if (!projectSections.some((s) => s.type === 'complete')) {
            const secId = crypto.randomUUID()
            newSections.push({ id: secId, projectId: id, name: 'Complete', type: 'complete', order: projectSections.length + newSections.length, deliverableType: null, deliverableQuota: null })
          }
        }
        // Single set() call for everything
        set((s) => ({
          activeProjectId: id,
          selectedTaskId: projectChanged ? null : prevSelectedTaskId,
          activeView,
          ...(newSections.length > 0 ? { sections: [...s.sections, ...newSections] } : {}),
        }))
        // Async side effects — fire after the single set() so they don't add to the sync burst
        if (!isWorkspace) get().getProjectMembers(id, { force: true })
        if (newSections.length > 0) {
          newSections.forEach((sec) => {
            try { const st = JSON.parse(localStorage.getItem('prjct-section-types') || '{}'); st[sec.id] = sec.type; localStorage.setItem('prjct-section-types', JSON.stringify(st)) } catch {}
            get()._persistAutoSection(sec)
          })
        }
        // Auto-spawn next occurrence for overdue recurring tasks that haven't been spawned yet
        setTimeout(() => {
          const today = new Date(); today.setHours(0, 0, 0, 0)
          get().tasks
            .filter((t) => t.projectId === id && !t.completed && t.recurrence?.freq && !t.recurrence.nextSpawned && t.dueDate && new Date(t.dueDate + 'T00:00:00') < today)
            .forEach((t) => get().spawnNextRecurrence(t.id))
        }, 0)
      },
      setActiveView: (view) => {
        const { activeProjectId, _viewPrefs } = get()
        const updated = { ...(_viewPrefs || {}), ...(activeProjectId ? { [activeProjectId]: view } : {}) }
        if (activeProjectId) { try { localStorage.setItem('prjct-view-prefs', JSON.stringify(updated)) } catch {} }
        set({ activeView: view, _viewPrefs: updated })
      },
      setSelectedTask: (id) => set({ selectedTaskId: id }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setTheme: (theme) => set({ theme }),
      setAccentColor: (color) => {
        document.documentElement.style.setProperty('--color-accent', color)
        set({ accentColor: color })
      },

      // ── Sync health helpers ───────────────────────────────────────────────────
      _writeGeneration: 0,
      _realtimeStatus: 'idle', // 'idle' | 'connecting' | 'connected' | 'degraded' | 'offline'
      _realtimeStatusAt: 0,
      _lastPing: null, // { ok, latencyMs, error, at }
      // Honest sync state, distinct from realtime/WS status. Drives the health indicator so a
      // stuck queue can't masquerade as "Synced" just because the websocket is still joined.
      //   'idle'              — nothing queued / everything synced
      //   'offline-queued'    — writes queued, REST genuinely unreachable (lock-free probe failed)
      //   'stuck-reconnecting'— auth-lock wedge confirmed; draining via the lock-free path
      _syncState: 'idle',
      // Forensic snapshots captured at the moment a transient write fails. Lets the
      // diagnostic panel answer the only question that matters when "Connected" is
      // green but every write times out: were reads still working, was the realtime
      // channel actually 'joined', and where in the request did the time go?
      _writeFailures: [], // [{at, label, meta, ping, realtime, recentRestCalls, ...}] — newest first, max 5
      _writeStart: () => {
        const gen = ++get()._writeGeneration
        set((s) => ({ _inFlight: s._inFlight + 1, _writeGeneration: gen }))
        // Safety net: if THIS write's generation is still the latest after 30s, force-reset.
        // 30s covers worst-case 2 writes × 20s WRITE_TIMEOUT_MS with the concurrency limiter.
        // Longer than that and the queue's own retry/reconnect machinery is authoritative —
        // keeping the spinner up past 30s just looks broken to the user.
        setTimeout(() => {
          if (get()._inFlight > 0 && get()._writeGeneration === gen) {
            console.warn('[sync] force-resetting stuck _inFlight counter (gen', gen, ')')
            set({ _inFlight: 0 })
            _inFlightEntityIds.clear()
            window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Save timed out — changes may not have been saved.' } }))
          }
        }, 30000)
      },
      _writeOk: () => set((s) => ({ _inFlight: Math.max(0, s._inFlight - 1) })),
      // critical=true  → data was discarded / won't auto-sync → show red error UI
      // critical=false → transient failure, data still queued / retrying → silent
      // meta: { table, op, entityId, entityLabel, code } — flows through to the
      // diagnostic panel so errors read as "Couldn't save 'Redesign hero'" instead of generic.
      _writeFail: (msg, critical = true, meta = null) => {
        if (critical) console.error('[sync:writeFail]', msg, meta || '', '| inFlight:', get()._inFlight, '[CRITICAL]')
        else console.warn('[sync:writeFail]', msg, meta || '', '| inFlight:', get()._inFlight, '[transient]')
        set((s) => ({ _inFlight: Math.max(0, s._inFlight - 1) }))
        // Always capture forensics — permanent fails used to be silent here, which meant
        // the panel's "Ping at failure" was always from an unrelated transient write and
        // we had no record of what was happening at the moment of a toast-level failure.
        // Buffer is bounded to 5 and the ping race has a 6s hard deadline, so capturing
        // on every fail is cheap. The severity field on the snapshot lets the panel
        // distinguish "this matches the toast you just saw" from "queued, retrying".
        const severity = critical ? 'permanent' : 'transient'
        get()._captureWriteForensics(msg, meta, severity).catch(() => {})
        // Start the active ping poll only for transient fails — permanent fails already
        // dequeued the entry, so there's nothing for the poll to flush.
        if (!critical) get()._startPingPoll()
        if (critical) {
          const code = meta?.code
          const label = code ? FATAL_PG_LABELS[code] : null
          const suffix = code ? ` (${code}${label ? ` — ${label}` : ''})` : ''
          window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: msg + suffix, meta } }))
        }
      },

      // Force-reconnect realtime by tearing down all channels and re-subscribing with a
      // fresh sid. _setupRealtime already does this work; we just rate-limit and delegate.
      _kickRealtime: () => {
        if (!supabase) return
        const now = Date.now()
        if (now - _lastRealtimeKickAt < 30000) return // rate-limit: at most once per 30s
        _lastRealtimeKickAt = now
        const projectIds = get().projects.map((p) => p.id)
        if (!projectIds.length) return
        console.info('[sync] kicking realtime — write timed out, forcing fresh channel subscriptions')
        get()._setupRealtime(projectIds)
      },

      // Last-resort recovery from a confirmed in-tab auth-lock wedge that the lock-free flush
      // couldn't fully drain. A wedged GoTrueClient can only be reset by constructing a new one,
      // which a page reload does. Safe because the write queue persists in localStorage and
      // _applySession re-flushes it on load — nothing queued is lost. Heavily guarded:
      //   • only while genuinely stuck (caller has confirmed wedge + drained what it could)
      //   • never mid-upload (XHR would be cancelled) or with in-flight in-memory writes
      //   • rate-limited to once per 5 min to prevent reload loops
      // Also used by the diagnostic panel's "Reconnect" action for manual recovery.
      _recoverFromAuthWedge: () => {
        const now = Date.now()
        if (now - _lastWedgeReloadAt < 5 * 60 * 1000) {
          console.warn('[sync] wedge reload suppressed — already reloaded within 5 min')
          return
        }
        if (_activeUploads > 0 || get()._inFlight > 0) {
          console.info('[sync] wedge reload deferred — upload or write in flight')
          return
        }
        if (typeof window === 'undefined') return
        _lastWedgeReloadAt = now
        console.warn('[sync] auth-lock wedge unrecoverable in-tab — reloading to reset the auth client (queue persists and replays on load)')
        window.location.reload()
      },

      // Active liveness probe. While the queue has writes that have been stuck for >15s,
      // ping the DB every 10s. First successful ping triggers a queue flush — this is the
      // missing recovery path on flaky connections where neither 'online' nor CHANNEL_ERROR
      // events ever fire.
      _startPingPoll: () => {
        if (_pingPollTimer) return // already polling
        const tick = async () => {
          // Stop polling if no stale writes remain.
          const queue = loadQueue()
          const now = Date.now()
          const stale = queue.filter((e) => now - (e.ts || now) > 15000)
          if (stale.length === 0) {
            // Queue drained — clear any stuck/queued state and stop polling.
            _wedgeRecoveryAttempts = 0
            if (get()._syncState !== 'idle') set({ _syncState: 'idle' })
            get()._stopPingPoll()
            return
          }
          // Hard-deadline the ping itself — pingHealth's AbortController does not
          // reliably cancel slow Supabase fetches on flaky links, so an unraced
          // await can hang forever and paralyze the whole poll. 6s is comfortably
          // past pingHealth's own 5s abort; if 'tick-deadline-6s' shows up in logs
          // it means the abort didn't honor and the underlying fetch is still
          // pending in the background.
          let ping
          try {
            ping = await Promise.race([
              get().pingHealth(),
              new Promise((r) => setTimeout(() => r({ ok: false, error: 'tick-deadline-6s' }), 6000)),
            ])
          } catch (err) {
            ping = { ok: false, error: err?.message || 'ping-threw' }
          }
          // Zombie-WS detection. If REST is reachable (ping ok) but NO realtime channel
          // is actually 'joined', the socket is in the "joined-but-silent" phoenix state
          // that makes other users' changes stop arriving until a manual refresh — the
          // core "stale until refresh" multiplayer symptom. Require 2 consecutive ticks
          // (avoid kicking on a brief mid-rejoin sample), then force a fresh channel
          // rebuild on its own rate-limit.
          try {
            const chans = get()._realtimeChannels || []
            const allDead = chans.length > 0 && !chans.some((ch) => ch?.state === 'joined')
            if (ping?.ok && allDead) {
              _zombieTicks++
              if (_zombieTicks >= 2 && Date.now() - _lastZombieKickAt > 30000) {
                _lastZombieKickAt = Date.now()
                _zombieTicks = 0
                const pids = get().projects.map((p) => p.id)
                if (pids.length) {
                  console.warn('[sync] realtime zombie detected (REST ok, no channel joined) — rebuilding channels')
                  get()._setupRealtime(pids)
                }
              }
            } else {
              _zombieTicks = 0
            }
          } catch { /* channel introspection is best-effort */ }

          const oldestStaleMs = Math.max(...stale.map((e) => now - (e.ts || now)))

          // Auth-lock wedge detection + escalation — the INVERSE of the zombie case above.
          // The websocket is healthy (a channel is joined) and we're online, but the client-routed
          // ping is failing and writes are piling up: the signature of the in-tab gotrue auth-lock
          // deadlock where every REST call through supabase-js hangs pre-fetch. Confirm with a
          // lock-free probe, then drain via the lock-free path (which bypasses the wedged client).
          try {
            const chans = get()._realtimeChannels || []
            const anyJoined = chans.some((ch) => ch?.state === 'joined')
            const wedgeSuspected = !_wedgeBusy && !ping?.ok && (typeof navigator === 'undefined' || navigator.onLine) && anyJoined && oldestStaleMs > WEDGE_THRESHOLD_MS
            if (wedgeSuspected) {
              _wedgeBusy = true
              try {
                const { _authToken, currentUser } = get()
                const probe = (_authToken && currentUser?.id)
                  ? await rawRestProbe({ token: _authToken, userId: currentUser.id, timeoutMs: 5000 })
                  : { ok: false, error: 'no-cached-token' }

                if (!probe.ok) {
                  // REST is genuinely unreachable — queueing IS the correct behavior. Surface an
                  // honest offline state instead of escalating; the 'online' handler will recover.
                  _wedgeRecoveryAttempts = 0
                  if (get()._syncState !== 'offline-queued') set({ _syncState: 'offline-queued' })
                  console.info(`[sync] writes stuck but REST unreachable (lock-free probe failed: ${probe.error}) — staying queued`)
                  return
                }

                // Lock-free reads succeed while the client path is wedged → confirmed auth-lock wedge.
                set({ _syncState: 'stuck-reconnecting' })
                console.warn(`[sync] auth-lock wedge confirmed (client ping ${ping?.error || 'failed'}, lock-free probe ok ${probe.latencyMs}ms) — draining via lock-free path`)
                let flushed = false
                try {
                  flushed = await flushStalePendingWritesRaw({ baseUrl: SUPABASE_URL, apikey: SUPABASE_ANON_KEY, token: _authToken })
                } catch (err) {
                  console.warn('[sync] lock-free flush failed:', err?.message)
                }
                if (flushed) window.dispatchEvent(new CustomEvent('prjct-write-synced'))

                if (pendingWriteCount() === 0) {
                  _wedgeRecoveryAttempts = 0
                  set({ _syncState: 'idle' })
                } else {
                  _wedgeRecoveryAttempts++
                  if (_wedgeRecoveryAttempts >= WEDGE_RAW_FLUSH_MAX_CYCLES) {
                    // Lock-free flush couldn't drain it either — last resort: guarded reload, which
                    // constructs a fresh GoTrueClient and replays the durable queue on load.
                    get()._recoverFromAuthWedge()
                  }
                }
              } finally {
                _wedgeBusy = false
              }
              return
            }
          } catch (err) { _wedgeBusy = false; console.warn('[sync] wedge detection error:', err?.message) }

          // Two paths to a flush:
          //   1. Fast path — ping ok and under 5s (matches pingHealth's own abort).
          //      Anything that returns under 5s is by definition not a hung link.
          //   2. Watchdog — queue has been stale for >60s. Per-write 20s timeout +
          //      MAX_CONCURRENT_WRITES=2 cap damage if the connection is dead, and
          //      _flushInProgress single-flight prevents stacking. Cost of attempting
          //      on a bad link is bounded; cost of NOT attempting is permanent stall.
          const fastPath = ping?.ok && ping.latencyMs < 5000
          const watchdog = !fastPath && oldestStaleMs > 60000
          if (!fastPath && !watchdog) return

          const reason = fastPath
            ? `ping ok (${ping.latencyMs}ms)`
            : `watchdog — oldest stale ${Math.round(oldestStaleMs / 1000)}s, ping ${ping?.ok ? `${ping.latencyMs}ms` : ping?.error || 'failed'}`
          console.info(`[sync] ${reason} — flushing ${stale.length} stale write(s)`)
          try {
            const flushed = await flushStalePendingWrites(supabase)
            if (flushed) {
              window.dispatchEvent(new CustomEvent('prjct-write-synced'))
              if (pendingWriteCount() === 0 && get()._syncState !== 'idle') set({ _syncState: 'idle' })
            }
          } catch (err) {
            console.warn('[sync] flush after recovery failed:', err?.message)
          }
        }
        // Run once immediately so the user doesn't wait 10s before the first probe,
        // then every 10s while stale writes remain.
        tick()
        _pingPollTimer = setInterval(tick, 10000)
      },

      _stopPingPoll: () => {
        if (_pingPollTimer) {
          clearInterval(_pingPollTimer)
          _pingPollTimer = null
        }
      },

      // Snapshot the world at the moment a write failed. Runs a fresh ping (raced
      // against a 4s deadline so a hung client can't stall capture forever), reads
      // realtime channel state directly off the Phoenix channel objects (the source
      // of truth, not the cached _realtimeStatus label), and pulls the last 5 Supabase
      // REST timing entries. Buffers up to 5 records and mirrors them to
      // window.__prjctSyncForensics for live inspection from devtools.
      _captureWriteForensics: async (label, meta, severity = 'transient') => {
        const at = Date.now()
        const realtimeStatusAt = get()._realtimeStatusAt
        const channels = (get()._realtimeChannels || []).map((ch) => ({
          topic: ch?.topic ?? null,
          state: ch?.state ?? null, // 'closed' | 'errored' | 'joined' | 'joining' | 'leaving'
          joinedOnce: ch?.joinedOnce ?? null,
        }))

        let ping = null
        try {
          // 6s — comfortably longer than pingHealth's own 5s abort, so a slow-but-alive
          // connection resolves with a real latency reading instead of a false 'deadline'.
          ping = await Promise.race([
            get().pingHealth(),
            new Promise((r) => setTimeout(() => r({ ok: false, error: 'forensics-ping-deadline-6s' }), 6000)),
          ])
        } catch (err) {
          ping = { ok: false, error: err?.message || 'ping-threw' }
        }

        let recentRestCalls = []
        try {
          // Last 15 — earlier capture used 5 and the actual write attempt got pushed out
          // of the buffer by the panel's 30s pings + repeat profile fetches. 15 is enough
          // to catch any tasks/projects write that reached the network layer.
          recentRestCalls = performance.getEntriesByType('resource')
            .filter((e) => /\/rest\/v1\//.test(e.name))
            .slice(-15)
            .map((e) => ({
              url: e.name.replace(/(\?[^#]*)?(#.*)?$/, (_, q) => (q ? '?…' : '')),
              startMs: Math.round(e.startTime),
              durationMs: Math.round(e.duration),
              // Time-to-first-byte: the gap between request leaving the browser and the
              // server's first response byte. Huge TTFB = server hanging on the request.
              ttfbMs: e.responseStart > 0 ? Math.round(e.responseStart - e.requestStart) : null,
              connectMs: e.connectEnd > 0 ? Math.round(e.connectEnd - e.connectStart) : null,
              transferSize: e.transferSize ?? null,
              decodedBodySize: e.decodedBodySize ?? null,
            }))
        } catch {}

        // rawFetch ring buffer — unlike the Performance API above, this records fetches that
        // were ABORTED before completing (e.g. reason 'safety-net (25000ms)'). If REST is blocked
        // pre-fetch at the gotrue auth lock, this buffer shows NO recent entries; if fetches start
        // and hang, it shows aborts. The single signal the Performance API can't give us.
        let recentClientFetches = []
        try { recentClientFetches = getRecentFetches().slice(-15) } catch {}

        // Lock-free probe: hits PostgREST directly with the cached bearer, bypassing the supabase
        // client's auth lock. If this succeeds while the client-routed ping above failed, the fault
        // is the in-tab auth-lock wedge — not the network. Raced against its own 5s deadline.
        let rawProbe = null
        try {
          const { _authToken, currentUser } = get()
          if (_authToken && currentUser?.id) {
            rawProbe = await rawRestProbe({ token: _authToken, userId: currentUser.id, timeoutMs: 5000 })
          } else {
            rawProbe = { ok: false, error: 'no-cached-token' }
          }
        } catch (err) {
          rawProbe = { ok: false, error: err?.message || 'probe-threw' }
        }

        // Verdict: lock-free reads OK + client path dead ⇒ auth-lock wedge; both dead ⇒ network.
        let verdict = 'inconclusive'
        if (rawProbe?.ok && !ping?.ok) verdict = 'auth-lock-wedge'
        else if (!rawProbe?.ok && !ping?.ok) verdict = 'network-or-server'

        const failure = {
          at,
          label,
          severity, // 'permanent' (toast fired, data discarded/restored) | 'transient' (queued, retrying)
          meta: meta || null,
          ping,
          rawProbe,
          verdict,
          realtime: {
            status: get()._realtimeStatus,
            statusAgeMs: realtimeStatusAt ? at - realtimeStatusAt : null,
            channels,
          },
          online: typeof navigator !== 'undefined' ? navigator.onLine : null,
          visibility: typeof document !== 'undefined' ? document.visibilityState : null,
          pendingQueueCount: pendingWriteCount(),
          recentRestCalls,
          recentClientFetches,
        }

        set((s) => ({ _writeFailures: [failure, ...(s._writeFailures || [])].slice(0, 5) }))
        try { window.__prjctSyncForensics = get()._writeFailures } catch {}
        return failure
      },

      // After flushStalePendingWrites lands queued INSERTs, the queue dequeues them but
      // _pendingSyncIds isn't touched (the queue lives in writeQueue.js, the array lives here).
      // Stale IDs that are no longer in the queue keep tasks showing the 'pending' badge
      // even though the row IS on the server. This trims _pendingSyncIds to only contain
      // IDs that still have a pending INSERT in the queue.
      _reconcilePendingSyncIds: () => {
        const stillQueued = new Set(
          loadQueue().filter((e) => e.op === 'insert' && e.data?.id).map((e) => e.data.id)
        )
        set((s) => {
          const trimmed = s._pendingSyncIds.filter((id) => stillQueued.has(id))
          if (trimmed.length === s._pendingSyncIds.length) return {}
          return { _pendingSyncIds: trimmed }
        })
      },

      // Lightweight DB health check. Uses a no-op SELECT against the user's own profile
      // row (bypasses RLS complexity and always exists after login).
      pingHealth: async () => {
        if (!supabase) return { ok: false, error: 'No client' }
        const { currentUser } = get()
        if (!currentUser?.id) return { ok: false, error: 'Not signed in' }
        const started = performance.now()
        try {
          const ctrl = new AbortController()
          const tid = setTimeout(() => ctrl.abort(), 5000)
          const { error } = await supabase
            .from('profiles').select('id').eq('id', currentUser.id).abortSignal(ctrl.signal).maybeSingle()
          clearTimeout(tid)
          const latencyMs = Math.round(performance.now() - started)
          const result = error
            ? { ok: false, latencyMs, error: error.message || String(error), at: Date.now() }
            : { ok: true, latencyMs, at: Date.now() }
          set({ _lastPing: result })
          return result
        } catch (err) {
          const latencyMs = Math.round(performance.now() - started)
          const result = { ok: false, latencyMs, error: err?.message || 'ping failed', at: Date.now() }
          set({ _lastPing: result })
          return result
        }
      },

      // Manual retry — flushes the persistent write queue and reloads data if anything landed.
      // Reset any stuck _inFlight counter first — timed-out writes leave it elevated,
      // blocking both new writes and the error UI that reveals the Retry button.
      retryPendingWrites: async () => {
        if (!supabase) return
        set({ _inFlight: 0 })
        _inFlightEntityIds.clear()
        try {
          // Use the same 20s timeout as normal writes — the old 6s caused false failures
          // when the connection was just slow, making retry less reliable than a page refresh.
          const flushed = await flushStalePendingWrites(supabase)
          // Always reload — even if queue was empty, _pendingSyncIds may still have
          // stale IDs from writes that already landed (e.g. optimistic task creates).
          const { currentUser } = get()
          if (currentUser?.id) await get().loadUserData(currentUser.id)
          // Permanent failures (RLS denial, fatal Postgres codes) dequeue themselves before retry runs,
          // so flushed=false alone doesn't mean the retry failed. If the queue is drained after this pass,
          // the user-perceived state IS in sync — fire 'synced' so the error indicator clears stale entries.
          // The handler filters errors by timestamp, so any new error fired during this retry is preserved.
          if (pendingWriteCount() === 0) {
            window.dispatchEvent(new CustomEvent('prjct-write-synced'))
          }
          return flushed
        } catch (e) {
          console.error('[retryPendingWrites]', e)
          return false
        }
      },

      // Manual retry for a confirmed auth-lock wedge. The normal retryPendingWrites() routes
      // through the supabase client, which is exactly what's wedged — it would just hang. This
      // drains via the lock-free PostgREST path instead. Used by the diagnostic panel's Retry
      // button when _syncState === 'stuck-reconnecting'.
      retryPendingWritesRaw: async () => {
        const { _authToken } = get()
        if (!_authToken) return false
        try {
          const flushed = await flushStalePendingWritesRaw({ baseUrl: SUPABASE_URL, apikey: SUPABASE_ANON_KEY, token: _authToken })
          if (pendingWriteCount() === 0) {
            _wedgeRecoveryAttempts = 0
            set({ _syncState: 'idle' })
            window.dispatchEvent(new CustomEvent('prjct-write-synced'))
          }
          return flushed
        } catch (e) {
          console.error('[retryPendingWritesRaw]', e)
          return false
        }
      },

      // ── Undo / Redo ───────────────────────────────────────────────────────────
      _snapshot: () => {
        const { tasks, sections, projects } = get()
        set((s) => ({ _history: [...s._history.slice(-30), { tasks, sections, projects }], _future: [] }))
      },
      // Sync a target task/section state to the DB after undo/redo.
      // Each diff row goes through retryWrite so transient failures (network, contention)
      // are queued + retried instead of silently dropped — a previous bug class where an undo
      // would visually revert but the DB drifted because the .then((error) => console.error) path
      // was the only failure handler.
      _syncSnapshot: (target, current) => {
        if (!supabase) return
        const curTaskMap = new Map(current.tasks.map((t) => [t.id, t]))
        const tgtTaskMap = new Map(target.tasks.map((t) => [t.id, t]))
        const stamp = Date.now()

        // Update tasks that changed
        for (const t of target.tasks) {
          const cur = curTaskMap.get(t.id)
          if (!cur) {
            // Re-insert task that was deleted (e.g. undo deleteTask, redo addTask).
            // keepOnFail: true — pre-generated UUID makes this idempotent (23505 = success).
            retryWrite({
              id: `snap-task-insert-${t.id}-${stamp}`,
              table: 'tasks',
              op: 'insert',
              keepOnFail: true,
              data: {
                id: t.id, title: t.title, description: t.description, project_id: t.projectId,
                section_id: t.sectionId, parent_id: t.parentId, assignee_id: t.assigneeId,
                due_date: t.dueDate, start_date: t.startDate, priority: t.priority, tags: t.tags,
                subtasks: t.subtasks, comments: t.comments, attachments: t.attachments,
                recurrence: t.recurrence || null, completed: t.completed, completed_at: t.completedAt || null,
                deliverable_type: t.deliverableType || null, position: t.position ?? 0,
              },
            }, supabase, {
              onFail: (e) => console.warn('[snapshot] task re-insert queued:', t.id, e?.message),
            })
          } else if (JSON.stringify(cur) !== JSON.stringify(t)) {
            // Exclude attachments/comments/subtasks — managed by updateTask directly; undo shouldn't overwrite them
            retryWrite({
              id: `snap-task-update-${t.id}-${stamp}`,
              table: 'tasks',
              op: 'update',
              filter: { eq: { id: t.id } },
              data: {
                title: t.title, description: t.description, section_id: t.sectionId,
                parent_id: t.parentId, assignee_id: t.assigneeId, due_date: t.dueDate,
                start_date: t.startDate, priority: t.priority, tags: t.tags,
                recurrence: t.recurrence || null, completed: t.completed, completed_at: t.completedAt || null,
                deliverable_type: t.deliverableType || null, position: t.position ?? 0,
              },
            }, supabase, {
              onFail: (e) => console.warn('[snapshot] task update queued:', t.id, e?.message),
            })
          }
        }

        // Delete tasks that shouldn't exist in target
        for (const cur of current.tasks) {
          if (!tgtTaskMap.has(cur.id)) {
            retryWrite({
              id: `snap-task-delete-${cur.id}-${stamp}`,
              table: 'tasks',
              op: 'delete',
              filter: { eq: { id: cur.id } },
            }, supabase, {
              onFail: (e) => console.warn('[snapshot] task delete queued:', cur.id, e?.message),
            })
          }
        }

        // Sync changed sections
        const curSecMap = new Map(current.sections.map((s) => [s.id, s]))
        for (const s of target.sections) {
          const cur = curSecMap.get(s.id)
          if (cur && (cur.name !== s.name || cur.order !== s.order || cur.type !== s.type)) {
            retryWrite({
              id: `snap-section-update-${s.id}-${stamp}`,
              table: 'sections',
              op: 'update',
              filter: { eq: { id: s.id } },
              data: { name: s.name, order: s.order, type: s.type },
            }, supabase, {
              onFail: (e) => console.warn('[snapshot] section update queued:', s.id, e?.message),
            })
          }
        }
      },

      undo: () => {
        const { _history, _future, tasks, sections, projects } = get()
        if (!_history.length) return false
        const prev = _history[_history.length - 1]
        set({ ...prev, _history: _history.slice(0, -1), _future: [{ tasks, sections, projects }, ..._future.slice(0, 29)] })
        get()._syncSnapshot(prev, { tasks, sections, projects })
        return true
      },
      redo: () => {
        const { _history, _future, tasks, sections, projects } = get()
        if (!_future.length) return false
        const next = _future[0]
        set({ ...next, _history: [..._history.slice(-29), { tasks, sections, projects }], _future: _future.slice(1) })
        get()._syncSnapshot(next, { tasks, sections, projects })
        return true
      },

      // ── Tasks ─────────────────────────────────────────────────────────────────
      addTask: (data) => {
        get()._snapshot()
        const id = supabase ? crypto.randomUUID() : 't' + Date.now()
        const section = get().sections.find((s) => s.id === data.sectionId)
        const position = get().tasks.filter((t) => t.sectionId === data.sectionId).length
        const task = {
          id,
          title: data.title || 'Untitled task',
          sectionId: data.sectionId || null,
          projectId: data.projectId,
          parentId: data.parentId || null,
          assigneeId: data.assigneeId !== undefined ? data.assigneeId : (get().currentUser?.id || null),
          dueDate: data.dueDate || null,
          startDate: data.startDate || null,
          priority: data.priority || 'none',
          description: data.description || '',
          attachments: data.attachments || [],
          subtasks: [],
          tags: data.tags || [],
          recurrence: data.recurrence || null,
          completed: false,
          createdAt: new Date().toISOString(),
          deliverableType: section?.deliverableType || null,
          contentSizes: data.contentSizes || [],
          position,
        }
        // Add to _pendingSyncIds immediately so loadUserData never wipes this task
        // before the INSERT is confirmed — even if called within the first 20s attempt window.
        set((s) => ({ tasks: [...s.tasks, task], _pendingSyncIds: [...s._pendingSyncIds, id] }))
        // Notify assignee (cross-user) if assigned to someone other than the current user
        if (task.assigneeId && task.assigneeId !== get().currentUser?.id) {
          const byName = get().currentUser?.name || 'Someone'
          get().notifyUser(task.assigneeId, { type: 'assignment', title: `${byName} assigned you a task`, body: task.title, taskId: id, projectId: task.projectId })
        }
        if (supabase) {
          const row = {
            id, title: task.title, description: task.description, project_id: task.projectId, section_id: task.sectionId,
            parent_id: task.parentId,
            assignee_id: task.assigneeId, due_date: task.dueDate, start_date: task.startDate,
            priority: task.priority, tags: task.tags, subtasks: [], comments: [], attachments: task.attachments,
            recurrence: task.recurrence || null,
            deliverable_type: task.deliverableType || null,
            content_size: task.contentSizes?.length ? task.contentSizes : null,
            position: task.position ?? 0,
            created_by: get().currentUser?.id, completed: false,
          }
          get()._writeStart()
          // Use retryWrite (persistent queue) + keepOnFail so the INSERT survives page close.
          // Pre-generated UUID makes this idempotent — 23505 (duplicate key) = already saved.
          // NEVER delete the task on network failure — keep it visible + queued for auto-retry.
          retryWrite(
            { id: `add-task-${id}`, table: 'tasks', op: 'insert', data: row, select: 'id', keepOnFail: true },
            supabase,
            {
              onOk: () => {
                get()._writeOk()
                set((s) => ({ _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== id) }))
              },
              // onRetryOk fires when a background retry succeeds (first attempt already called onFail).
              // This is the fix for _pendingSyncIds getting stuck: when the first INSERT attempt
              // fails, onFail fires (calledBack=true). If a later attempt succeeds, onOk is never
              // called, so _pendingSyncIds would stay stuck forever. onRetryOk cleans it up.
              onRetryOk: () => {
                set((s) => ({ _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== id) }))
              },
              onBlocked: () => {
                // Genuine RLS block — task cannot be saved, remove it
                console.error('[addTask] INSERT blocked by RLS')
                set((s) => ({
                  tasks: s.tasks.filter((t) => t.id !== id),
                  _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== id),
                }))
                get()._writeFail(`Couldn't create "${task.title}" — permission denied.`, true, { table: 'tasks', op: 'insert', entityId: id, entityLabel: task.title })
              },
              onFail: (e, { permanent } = {}) => {
                // Network/DB error — task stays in store and queue, will auto-retry.
                // _pendingSyncIds already has the id (set at creation), so no update needed here.
                console.warn('[addTask] INSERT failed, task kept locally for retry:', e?.message)
                // Only surface to UI if permanently discarded (schema error etc.)
                get()._writeFail(`Connection issue — "${task.title}" saved locally and will sync when connection is restored.`, permanent, { table: 'tasks', op: 'insert', entityId: id, entityLabel: task.title, code: e?.code })
              },
            }
          )
        }
        return id
      },

      updateTask: (id, updates) => {
        const byName = get().currentUser?.name || 'Someone'
        const currentUserId = get().currentUser?.id
        const task = get().tasks.find((t) => t.id === id)
        // Notify new assignee (cross-user) when assignment changes
        if (updates.assigneeId != null && updates.assigneeId !== currentUserId && task && updates.assigneeId !== task.assigneeId) {
          get().notifyUser(updates.assigneeId, { type: 'assignment', title: `${byName} assigned you a task`, body: task?.title, taskId: id, projectId: task?.projectId })
        }
        // Notify existing assignee of due date change
        if (updates.dueDate !== undefined && task && task.assigneeId && task.assigneeId !== currentUserId && task.dueDate !== updates.dueDate) {
          const dateStr = updates.dueDate
            ? new Date(updates.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : 'removed'
          get().notifyUser(task.assigneeId, { type: 'due_date', title: `Due date updated: "${task.title}"`, body: `Changed to ${dateStr} by ${byName}`, taskId: id, projectId: task.projectId })
        }

        // Auto-track activity for key field changes (skip when comments are being directly updated)
        let finalUpdates = updates
        if (updates.comments === undefined) {
          if (task) {
            const TRACKED = ['completed', 'dueDate', 'assigneeId', 'priority', 'sectionId']
            const activities = []
            for (const field of TRACKED) {
              if (updates[field] !== undefined && updates[field] !== task[field]) {
                activities.push({
                  id: 'act' + Date.now() + Math.random().toString(36).slice(2),
                  type: 'activity',
                  field,
                  from: task[field],
                  to: updates[field],
                  by: get().currentUser?.id,
                  byName: get().currentUser?.name,
                  at: new Date().toISOString(),
                })
              }
            }
            if (activities.length > 0) {
              finalUpdates = { ...updates, comments: [...(task.comments || []), ...activities] }
            }
          }
        }

        set((s) => ({ tasks: s.tasks.map((t) => t.id === id ? { ...t, ...finalUpdates } : t) }))
        if (!supabase) return
        const db = {}
        if (finalUpdates.title !== undefined) db.title = finalUpdates.title
        if (finalUpdates.description !== undefined) db.description = finalUpdates.description
        if (finalUpdates.sectionId !== undefined) db.section_id = finalUpdates.sectionId
        if (finalUpdates.assigneeId !== undefined) db.assignee_id = finalUpdates.assigneeId
        if (finalUpdates.dueDate !== undefined) db.due_date = finalUpdates.dueDate
        if (finalUpdates.startDate !== undefined) db.start_date = finalUpdates.startDate
        if (finalUpdates.priority !== undefined) db.priority = finalUpdates.priority
        if (finalUpdates.completed !== undefined) db.completed = finalUpdates.completed
        if (finalUpdates.tags !== undefined) db.tags = finalUpdates.tags
        if (finalUpdates.subtasks !== undefined) db.subtasks = finalUpdates.subtasks
        if (finalUpdates.comments !== undefined) db.comments = finalUpdates.comments
        if (finalUpdates.attachments !== undefined) db.attachments = finalUpdates.attachments
        if (finalUpdates.parentId !== undefined) db.parent_id = finalUpdates.parentId
        if (finalUpdates.recurrence !== undefined) db.recurrence = finalUpdates.recurrence
        if (finalUpdates.contentSizes !== undefined) db.content_size = finalUpdates.contentSizes
        if (finalUpdates.deliverableType !== undefined) db.deliverable_type = finalUpdates.deliverableType
        if (finalUpdates.completedAt !== undefined) db.completed_at = finalUpdates.completedAt
        if (finalUpdates.position !== undefined) db.position = finalUpdates.position
        if (Object.keys(db).length) {
          // Build a full DB snapshot of the current task state.
          // Writing full state (not just changed fields) ensures no partial write
          // can interleave with a stale retry to produce corrupted data.
          function buildFullSnapshot() {
            const t = get().tasks.find((x) => x.id === id)
            if (!t) return null
            const snap = { updated_at: new Date().toISOString() }
            const add = (dbKey, val) => { if (val !== undefined) snap[dbKey] = val }
            add('title', t.title)
            add('description', t.description)
            add('section_id', t.sectionId)
            add('assignee_id', t.assigneeId)
            add('due_date', t.dueDate)
            add('start_date', t.startDate)
            add('priority', t.priority)
            add('completed', t.completed)
            add('tags', t.tags)
            add('subtasks', t.subtasks)
            add('comments', t.comments)
            // Strip in-flight upload placeholders — never persist uploading/loading=true to DB
            // (if the page reloads mid-upload they would show as stuck spinners forever).
            // Also drop the transient `thumbUploading` flag — the original URL is fine to
            // persist while the thumb upload is still in flight; the field reappears on
            // the next render if the thumb upload eventually fails and the renderer
            // backfills again.
            const persistedAtts = t.attachments
              ? t.attachments
                  .filter(isPersistableAttachment)
                  .map((a) => {
                    if (!a.thumbUploading) return a
                    const { thumbUploading, ...rest } = a
                    return rest
                  })
              : t.attachments
            add('attachments', persistedAtts)
            add('parent_id', t.parentId)
            add('recurrence', t.recurrence)
            add('content_size', t.contentSizes)
            add('completed_at', t.completedAt ?? null)
            add('deliverable_type', t.deliverableType ?? null)
            add('position', t.position ?? 0)
            return snap
          }

          // Persist a full snapshot to localStorage immediately — survives page close
          // within the debounce window. Stable ID means queue holds only latest snapshot.
          const immediateSnap = buildFullSnapshot()
          if (immediateSnap) {
            enqueue({ id: `update-task-${id}`, table: 'tasks', op: 'update', data: immediateSnap, filter: { eq: { id } }, select: 'id' })
          }

          // Block realtime updates for this task immediately — not just when the debounce
          // fires. Without this, realtime from an earlier write (which had the attachment
          // stripped) can arrive during the 500ms debounce window and overwrite the
          // optimistic local state (e.g. a just-uploaded attachment URL), causing the
          // attachment to disappear permanently when buildFullSnapshot() runs at debounce time.
          _inFlightEntityIds.add(id)

          // Debounce the network write. Clear any pending timer so rapid changes
          // (e.g. typing) collapse into one request. At fire time, re-snapshot
          // to pick up all changes made during the debounce window.
          clearTimeout(_taskWriteTimers[id])
          // Fire the UPDATE, but if this task's INSERT hasn't confirmed yet, wait for it so
          // we don't race a 404 (the UPDATE would be "blocked"). The latest snapshot is
          // already durably queued (update-task-${id}), so the wait is bounded: re-check a
          // few times and fire as soon as the INSERT lands; after the cap, hand off to the
          // queue (entry stays; the realtime guard keeps the task protected) instead of
          // re-arming forever. We deliberately do NOT nudge a flush here — flushing an
          // UPDATE while the row doesn't exist yet would dequeue it as "blocked" and lose
          // the edit; the normal flush triggers run it safely once the row exists.
          const INSERT_WAIT_MAX = 3
          const fireTaskUpdate = (insertWaitAttempt) => {
            delete _taskWriteTimers[id]
            if (get()._pendingSyncIds.includes(id)) {
              if (insertWaitAttempt >= INSERT_WAIT_MAX) { _inFlightEntityIds.delete(id); return }
              _taskWriteTimers[id] = setTimeout(() => fireTaskUpdate(insertWaitAttempt + 1), 2000)
              return
            }
            const snap = buildFullSnapshot()
            if (!snap) { _inFlightEntityIds.delete(id); return }
            get()._writeStart()
            retryWrite(
              { id: `update-task-${id}`, table: 'tasks', op: 'update', data: snap, filter: { eq: { id } }, select: 'id' },
              supabase,
              {
                keepOnFail: true,
                onOk: () => { _inFlightEntityIds.delete(id); get()._writeOk() },
                onBlocked: () => { _inFlightEntityIds.delete(id); get()._writeFail(`Couldn't save "${task?.title || 'task'}" — permission denied.`, true, { table: 'tasks', op: 'update', entityId: id, entityLabel: task?.title }) },
                onFail: (e, { permanent } = {}) => { _inFlightEntityIds.delete(id); if (permanent) console.error('updateTask:', e); else console.warn('updateTask (transient):', e?.message); get()._writeFail(`Failed to save "${task?.title || 'task'}".`, permanent, { table: 'tasks', op: 'update', entityId: id, entityLabel: task?.title, code: e?.code }) },
              }
            )
          }
          _taskWriteTimers[id] = setTimeout(() => fireTaskUpdate(0), 500)
        }
      },

      deleteTask: (id) => {
        get()._snapshot()
        const dead = new Set()
        const collect = (tid) => { dead.add(tid); get().tasks.forEach((t) => { if (t.parentId === tid) collect(t.id) }) }
        collect(id)
        const deadIds = [...dead]
        // Snapshot rows we're about to remove so we can roll back if the DELETE permanently fails.
        const removed = get().tasks.filter((t) => dead.has(t.id))
        // Look up the project once now — after the set() call below the task is gone from store,
        // so we'd lose the projectId needed for the self-heal check on RLS block.
        const rootTask = removed.find((t) => t.id === id) || removed[0]
        const projectAtDelete = rootTask ? get().projects.find((p) => p.id === rootTask.projectId) : null

        // Separate ghosts (locally-created rows whose INSERT never confirmed) from real
        // server-side rows. Ghosts can't be DELETEd server-side because they don't exist there —
        // a server DELETE would return 0 rows and the writeQueue's `select` check would mark
        // it as RLS-blocked, surfacing a misleading "permission denied" error and restoring
        // the ghost row to local state. Cancel the queued INSERT and skip server DELETE for ghosts.
        const queuedInsertIds = new Set(
          loadQueue()
            .filter((e) => e.table === 'tasks' && e.op === 'insert' && deadIds.includes(e.data?.id))
            .map((e) => e.data.id)
        )
        const pendingIds = new Set(get()._pendingSyncIds)
        const ghostIds = deadIds.filter((tid) => queuedInsertIds.has(tid) || pendingIds.has(tid))
        const realIds = deadIds.filter((tid) => !ghostIds.includes(tid))

        set((s) => ({
          tasks: s.tasks.filter((t) => !dead.has(t.id)),
          _pendingSyncIds: s._pendingSyncIds.filter((pid) => !ghostIds.includes(pid)),
        }))
        if (ghostIds.length) {
          cancelQueuedInserts('tasks', ghostIds)
        }

        if (!supabase || !realIds.length) return

        // Block realtime UPDATE events for these rows during the in-flight window so a
        // straggling event can't patch a deleted task back into local state.
        realIds.forEach((tid) => _inFlightEntityIds.add(tid))
        get()._writeStart()
        let selfHealed = false

        // retryWrite (persistent queue) instead of retryTransient — a failed DELETE must survive
        // page close, otherwise loadUserData on reconnect resurrects the task from the DB.
        const runDelete = () => {
          retryWrite(
            { id: `del-tasks-${realIds.join(',')}-${Date.now()}`, table: 'tasks', op: 'delete', filter: { in: { id: realIds } }, select: 'id' },
            supabase,
            {
              onOk: () => {
                realIds.forEach((tid) => _inFlightEntityIds.delete(tid))
                get()._writeOk()
              },
              onBlocked: async () => {
                // Self-heal once: if the user owns this project, the RLS denial is most likely a
                // missing project_members row (the add_owner_as_member trigger didn't fire, or the
                // row was lost). loadUserData already has repair logic for this exact case — re-run
                // it and try the delete again before giving up.
                const userId = get().currentUser?.id
                if (!selfHealed && userId && projectAtDelete?.ownerId === userId) {
                  selfHealed = true
                  console.warn('[deleteTask] permission denied on owned project — running self-heal')
                  try {
                    await get().loadUserData(userId)
                    runDelete()
                    return
                  } catch (e) {
                    console.error('[deleteTask] self-heal loadUserData failed:', e)
                    // Fall through to the standard restore + error path below.
                  }
                }
                // Permission denied — restore only the realIds (ghosts already handled and stay deleted).
                realIds.forEach((tid) => _inFlightEntityIds.delete(tid))
                const realRemoved = removed.filter((t) => realIds.includes(t.id))
                set((s) => ({ tasks: [...s.tasks, ...realRemoved] }))
                const label = realRemoved[0]?.title
                get()._writeFail(label ? `Couldn't delete "${label}" — permission denied.` : 'Permission denied — task delete blocked.', true, { table: 'tasks', op: 'delete', entityId: id, entityLabel: label })
              },
              onFail: (e, { permanent } = {}) => {
                realIds.forEach((tid) => _inFlightEntityIds.delete(tid))
                if (permanent) {
                  console.error('deleteTask:', e)
                  const realRemoved = removed.filter((t) => realIds.includes(t.id))
                  set((s) => ({ tasks: [...s.tasks, ...realRemoved] }))
                }
                const label = removed[0]?.title
                get()._writeFail(label ? `Failed to delete "${label}".` : 'Failed to delete task.', permanent, { table: 'tasks', op: 'delete', entityId: id, entityLabel: label, code: e?.code })
              },
            }
          )
        }
        runDelete()
      },

      duplicateTask: (id) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task) return null
        get()._snapshot()
        const newId = supabase ? crypto.randomUUID() : 't' + Date.now()
        const position = get().tasks.filter((t) => t.sectionId === task.sectionId).length
        const copy = {
          ...task,
          id: newId,
          title: task.title + ' (Copy)',
          createdAt: new Date().toISOString(),
          completed: false,
          completedAt: null,
          comments: [],
          subtasks: (task.subtasks || []).map((st) => ({ ...st, id: crypto.randomUUID() })),
          position,
        }
        set((s) => ({ tasks: [...s.tasks, copy], _pendingSyncIds: [...s._pendingSyncIds, newId] }))
        if (supabase) {
          const row = {
            id: newId, title: copy.title, description: copy.description, project_id: copy.projectId,
            section_id: copy.sectionId, parent_id: copy.parentId, assignee_id: copy.assigneeId,
            due_date: copy.dueDate, start_date: copy.startDate, priority: copy.priority,
            tags: copy.tags, subtasks: copy.subtasks, comments: [], attachments: copy.attachments,
            recurrence: copy.recurrence || null,
            deliverable_type: copy.deliverableType || null,
            content_size: copy.contentSizes?.length ? copy.contentSizes : null,
            position: copy.position ?? 0,
            created_by: get().currentUser?.id, completed: false,
          }
          get()._writeStart()
          // Persistent queue + keepOnFail so a transient failure can't lose the duplicate.
          // Without this, the local copy survives only in Zustand and gets wiped by the next
          // loadUserData — and any rename made between duplicate and refresh hits a missing row.
          retryWrite(
            { id: `dup-task-${newId}`, table: 'tasks', op: 'insert', data: row, select: 'id', keepOnFail: true },
            supabase,
            {
              onOk: () => {
                get()._writeOk()
                set((s) => ({ _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== newId) }))
              },
              onRetryOk: () => {
                set((s) => ({ _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== newId) }))
              },
              onBlocked: () => {
                console.error('[duplicateTask] INSERT blocked by RLS')
                set((s) => ({
                  tasks: s.tasks.filter((t) => t.id !== newId),
                  _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== newId),
                }))
                get()._writeFail(`Couldn't duplicate "${task.title}" — permission denied.`, true, { table: 'tasks', op: 'insert', entityId: newId, entityLabel: copy.title })
              },
              onFail: (e, { permanent } = {}) => {
                console.warn('[duplicateTask] INSERT failed, copy kept locally for retry:', e?.message)
                get()._writeFail(`Connection issue — duplicate of "${task.title}" saved locally and will sync when connection is restored.`, permanent, { table: 'tasks', op: 'insert', entityId: newId, entityLabel: copy.title, code: e?.code })
              },
            }
          )
        }
        return newId
      },

      // Copy one or more tasks to a different project / section, preserving all fields
      copyTasksTo: (taskIds, targetProjectId, targetSectionId) => {
        const { tasks, currentUser } = get()
        const targets = taskIds.map((id) => tasks.find((t) => t.id === id)).filter(Boolean)
        if (!targets.length) return
        get()._snapshot()
        const copies = targets.map((task) => {
          const newId = supabase ? crypto.randomUUID() : 't' + Date.now() + Math.random()
          return {
            ...task,
            id: newId,
            projectId: targetProjectId,
            sectionId: targetSectionId || null,
            completed: false,
            completedAt: null,
            createdAt: new Date().toISOString(),
            subtasks: (task.subtasks || []).map((st) => ({ ...st, id: crypto.randomUUID() })),
          }
        })
        set((s) => ({ tasks: [...s.tasks, ...copies], _pendingSyncIds: [...s._pendingSyncIds, ...copies.map((c) => c.id)] }))
        if (supabase) {
          copies.forEach((copy) => {
            get()._writeStart()
            retryTransient(
              () => supabase.from('tasks').insert({
                id: copy.id,
                title: copy.title,
                description: copy.description,
                project_id: copy.projectId,
                section_id: copy.sectionId,
                assignee_id: copy.assigneeId,
                due_date: copy.dueDate,
                start_date: copy.startDate,
                priority: copy.priority,
                tags: copy.tags,
                recurrence: copy.recurrence,
                subtasks: copy.subtasks,
                comments: [],
                attachments: copy.attachments,
                deliverable_type: copy.deliverableType,
                created_by: currentUser?.id,
                completed: false,
              }).select('id'),
              {
                onOk: () => { get()._writeOk(); set((s) => ({ _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== copy.id) })) },
                onBlocked: () => { set((s) => ({ tasks: s.tasks.filter((t) => t.id !== copy.id), _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== copy.id) })); get()._writeFail('Copy not saved — permission denied.') },
                onFail: (e, { permanent } = {}) => { console.error('copyTasksTo:', e); set((s) => ({ tasks: s.tasks.filter((t) => t.id !== copy.id), _pendingSyncIds: s._pendingSyncIds.filter((x) => x !== copy.id) })); get()._writeFail('Failed to copy task.', permanent) },
              }
            )
          })
        }
        return copies.map((c) => c.id)
      },

      moveTask: (taskId, toSectionId, afterTaskId = null) => {
        get()._snapshot()
        // Inherit the destination section's deliverableType so reporting counts correctly
        const destSection = get().sections.find((s) => s.id === toSectionId)
        const newDeliverableType = destSection?.deliverableType ?? null
        set((s) => {
          const task = s.tasks.find((t) => t.id === taskId)
          if (!task) return s
          const others = s.tasks.filter((t) => t.id !== taskId && t.sectionId === toSectionId)
          const moved = { ...task, sectionId: toSectionId, deliverableType: newDeliverableType }
          if (afterTaskId) { const idx = others.findIndex((t) => t.id === afterTaskId); others.splice(idx + 1, 0, moved) }
          else others.unshift(moved)
          return { tasks: [...s.tasks.filter((t) => t.sectionId !== toSectionId && t.id !== taskId), ...others] }
        })
        if (supabase) {
          // Capture ordered section tasks before the async debounce for position write
          const orderedSectionTasks = get().tasks.filter((t) => t.sectionId === toSectionId)
          // Route through updateTask: full snapshot + stable queue id + keepOnFail
          get().updateTask(taskId, { sectionId: toSectionId, deliverableType: newDeliverableType })
          // Position ordering: retried writes after updateTask's debounce window
          // retryTransient is sufficient — positions are recoverable from re-dragging
          setTimeout(() => {
            orderedSectionTasks.forEach((t, idx) => {
              retryTransient(() => supabase.from('tasks').update({ position: idx * 100 }).eq('id', t.id))
            })
          }, 600)
        }
      },

      toggleTask: (id, origin) => {
        get()._snapshot()
        const task = get().tasks.find((t) => t.id === id)
        const completed = !task?.completed
        const completedAt = completed ? new Date().toISOString() : null
        set((s) => ({ tasks: s.tasks.map((t) => t.id === id ? { ...t, completed, completedAt } : t) }))
        if (completed && task) {
          window.dispatchEvent(new CustomEvent('task-completed', { detail: origin || null }))
          // Spawn next occurrence for recurring tasks
          // Use first non-typed section — task.sectionId may already be 'complete' at this point
          // because executeCompletion calls moveTask before toggleTask
          if (task.recurrence?.freq && !task.recurrence.nextSpawned) {
            const nextDue = computeNextDueDate(task.recurrence, task.dueDate)
            const spawnSectionId = get().sections
              .filter((s) => s.projectId === task.projectId && !s.type)
              .sort((a, b) => a.order - b.order)[0]?.id || null
            const { nextSpawned: _ns, ...cleanRecurrence } = task.recurrence
            get().addTask({
              title: task.title,
              description: task.description,
              sectionId: spawnSectionId,
              projectId: task.projectId,
              assigneeId: task.assigneeId,
              priority: task.priority,
              tags: task.tags || [],
              dueDate: nextDue,
              recurrence: cleanRecurrence,
            })
          }
        }
        // Route through updateTask: builds full snapshot with stable queue id,
        // preventing dedup collisions that would overwrite pending changes
        get().updateTask(id, { completed, completedAt })
      },

      // ── Completion flow ───────────────────────────────────────────────────────
      _getProjectSection: (projectId, type) => {
        const existing = get().sections.find((s) => s.projectId === projectId && s.type === type)
        if (existing) return existing.id
        return get().addSection(projectId, type === 'approval' ? 'Approval' : 'Complete', type)
      },

      initiateCompletion: (taskId, origin) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return
        if (task.completed) { get().toggleTask(taskId, origin); return } // uncompleting — skip flow
        // Role-based auto-routing: owners → complete, members → approval.
        // Workspace ownership cascades: an owner of the parent workspace bypasses approval
        // on every child board, even if their explicit membership row says member/viewer.
        const projects = get().projects
        const project = projects.find((p) => p.id === task.projectId)
        const workspace = project?.parentId ? projects.find((p) => p.id === project.parentId) : null
        const isOwner = project?.ownerId === get().currentUser?.id
          || project?.role === 'owner'
          || workspace?.role === 'owner'
        get().executeCompletion(taskId, isOwner ? 'complete' : 'approval', origin, false)
      },

      executeCompletion: (taskId, destination, origin) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task || task.completed) return
        if (destination === 'approval') {
          // Single atomic write: move to approval section (not marked complete)
          get()._snapshot()
          const sectionId = get()._getProjectSection(task.projectId, 'approval')
          const originSectionId = task.sectionId // remember for rejection so it returns to the right place
          set((s) => ({ tasks: s.tasks.map((t) => t.id === taskId ? { ...t, sectionId, originSectionId } : t) }))
          get().updateTask(taskId, { sectionId })
          // Notify all project owners so they see the pending approval in their Inbox
          if (supabase) {
            const byName = get().currentUser?.name || 'Someone'
            // Retry the owner lookup on transient failure so a network blip doesn't
            // silently drop the approval notifications. Rows are captured in closure
            // (retryTransient's onOk carries no data) and notified once on success.
            let ownerRows = null
            retryTransient(
              async () => {
                const res = await supabase.from('project_members').select('user_id').eq('project_id', task.projectId).eq('role', 'owner')
                if (!res.error) ownerRows = res.data || []
                return res
              },
              {
                onOk: () => {
                  const currentUserId = get().currentUser?.id
                  ;(ownerRows || []).forEach((row) => {
                    if (row.user_id && row.user_id !== currentUserId) {
                      get().notifyUser(row.user_id, { type: 'approval_request', title: `${byName} sent a task for approval`, body: task.title, taskId, projectId: task.projectId })
                    }
                  })
                },
                onFail: (e) => console.warn('notify owners on approval — gave up after retries:', e?.message),
              }
            )
          }
        } else if (destination === 'complete') {
          // Single atomic write: move to complete section + mark completed
          get()._snapshot()
          const sectionId = get()._getProjectSection(task.projectId, 'complete')
          const completedAt = new Date().toISOString()
          set((s) => ({ tasks: s.tasks.map((t) => t.id === taskId ? { ...t, sectionId, completed: true, completedAt } : t) }))
          window.dispatchEvent(new CustomEvent('task-completed', { detail: origin || null }))
          if (task.recurrence?.freq && !task.recurrence.nextSpawned) {
            const nextDue = computeNextDueDate(task.recurrence, task.dueDate)
            const spawnSectionId = get().sections.filter((s) => s.projectId === task.projectId && !s.type).sort((a, b) => a.order - b.order)[0]?.id || null
            const { nextSpawned: _ns, ...cleanRecurrence } = task.recurrence
            get().addTask({ title: task.title, description: task.description, sectionId: spawnSectionId, projectId: task.projectId, assigneeId: task.assigneeId, priority: task.priority, tags: task.tags || [], dueDate: nextDue, recurrence: cleanRecurrence })
          }
          get().updateTask(taskId, { sectionId, completed: true, completedAt })
        } else {
          get().toggleTask(taskId, origin) // 'none' — complete in place
        }
      },

      approveTask: (taskId, origin) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return
        // Single atomic write: move to complete section + mark completed
        get()._snapshot()
        const sectionId = get()._getProjectSection(task.projectId, 'complete')
        const completedAt = task.completed ? task.completedAt : new Date().toISOString()
        const shouldComplete = !task.completed
        set((s) => ({ tasks: s.tasks.map((t) => t.id === taskId ? { ...t, sectionId, completed: true, completedAt } : t) }))
        if (shouldComplete) {
          window.dispatchEvent(new CustomEvent('task-completed', { detail: origin || null }))
          if (task.recurrence?.freq && !task.recurrence.nextSpawned) {
            const nextDue = computeNextDueDate(task.recurrence, task.dueDate)
            const spawnSectionId = get().sections.filter((s) => s.projectId === task.projectId && !s.type).sort((a, b) => a.order - b.order)[0]?.id || null
            const { nextSpawned: _ns, ...cleanRecurrence } = task.recurrence
            get().addTask({ title: task.title, description: task.description, sectionId: spawnSectionId, projectId: task.projectId, assigneeId: task.assigneeId, priority: task.priority, tags: task.tags || [], dueDate: nextDue, recurrence: cleanRecurrence })
          }
        }
        get().updateTask(taskId, { sectionId, completed: true, completedAt })
        // Notify assignee their task was approved
        if (task.assigneeId && task.assigneeId !== get().currentUser?.id) {
          const byName = get().currentUser?.name || 'Someone'
          get().notifyUser(task.assigneeId, { type: 'approval', title: `Task approved: "${task.title}"`, body: `Approved by ${byName}`, taskId, projectId: task.projectId })
        }
      },

      spawnNextRecurrence: (taskId) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task?.recurrence?.freq || task.recurrence.nextSpawned) return
        const { nextSpawned: _ns, ...cleanRecurrence } = task.recurrence
        const nextDue = computeNextDueDate(task.recurrence, task.dueDate)
        const spawnSectionId = get().sections.filter((s) => s.projectId === task.projectId && !s.type).sort((a, b) => a.order - b.order)[0]?.id || null
        get().addTask({ title: task.title, description: task.description, sectionId: spawnSectionId, projectId: task.projectId, assigneeId: task.assigneeId, priority: task.priority, tags: task.tags || [], dueDate: nextDue, recurrence: cleanRecurrence })
        get().updateTask(taskId, { recurrence: { ...task.recurrence, nextSpawned: true } })
      },

      resetCompletionPreference: () => {
        try { localStorage.removeItem('prjct-completion-pref') } catch {}
        set({ completionPreference: null })
      },

      rejectTask: (taskId, note) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return
        // Return to origin section (stored when task was sent to approval), or fall back to
        // first non-approval/non-complete section (includes misc, reels, graphics, etc.)
        const originSection = task.originSectionId
          ? get().sections.find((s) => s.id === task.originSectionId)
          : null
        const returnSection = originSection || get().sections
          .filter((s) => s.projectId === task.projectId && s.type !== 'approval' && s.type !== 'complete')
          .sort((a, b) => a.order - b.order)[0]
        const returnSectionId = returnSection?.id || task.sectionId
        const activity = {
          id: 'act' + Date.now() + Math.random().toString(36).slice(2),
          type: 'activity',
          field: 'rejected',
          from: null,
          to: note || '',
          by: get().currentUser?.id,
          byName: get().currentUser?.name,
          at: new Date().toISOString(),
        }
        // Pass comments explicitly to bypass auto-tracking (we handle it manually); clear originSectionId
        get().updateTask(taskId, { sectionId: returnSectionId, originSectionId: null, comments: [...(task.comments || []), activity] })
        // Notify assignee their task was rejected
        if (task.assigneeId && task.assigneeId !== get().currentUser?.id) {
          const byName = get().currentUser?.name || 'Someone'
          get().notifyUser(task.assigneeId, { type: 'rejection', title: `Task needs revisions: "${task.title}"`, body: note ? `${byName}: ${note}` : `Rejected by ${byName}`, taskId, projectId: task.projectId })
        }
      },

      saveProjectView: (projectId, name, viewState) => {
        const project = get().projects.find((p) => p.id === projectId)
        if (!project) return null
        const view = { id: crypto.randomUUID(), name, ...viewState, createdAt: new Date().toISOString() }
        get().updateProject(projectId, { views: [...(project.views || []), view] })
        return view.id
      },

      deleteProjectView: (projectId, viewId) => {
        const project = get().projects.find((p) => p.id === projectId)
        if (!project) return
        get().updateProject(projectId, { views: (project.views || []).filter((v) => v.id !== viewId) })
      },

      // Bulk ops fire one retryWrite per row (with the queue's slot limiter capping concurrency).
      // Per-row writes give independent retry/rollback and survive offline windows via
      // localStorage replay — single .in() updates produced silent partial-success states
      // when only some rows hit RLS.
      bulkToggleComplete: (ids) => {
        get()._snapshot()
        const completedAt = new Date().toISOString()
        set((s) => ({ tasks: s.tasks.map((t) => ids.includes(t.id) ? { ...t, completed: true, completedAt } : t) }))
        if (!supabase || !ids.length) return
        const stamp = Date.now()
        get()._bulkRunWrites(ids, 'tasks', 'update', { completed: true, completed_at: completedAt }, `bulk-complete-${stamp}`, 'Failed to complete tasks.')
      },

      bulkDeleteTasks: (ids) => {
        get()._snapshot()
        // Capture the rows so a permanently-rejected delete can be restored to the UI.
        const removed = get().tasks.filter((t) => ids.includes(t.id))
        // Cancel any pending INSERTs for these IDs so a reconnect doesn't recreate them
        cancelQueuedInserts('tasks', ids)
        set((s) => ({
          tasks: s.tasks.filter((t) => !ids.includes(t.id)),
          _pendingSyncIds: s._pendingSyncIds.filter((id) => !ids.includes(id)),
        }))
        if (!supabase || !ids.length) return
        const stamp = Date.now()
        get()._bulkRunWrites(ids, 'tasks', 'delete', null, `bulk-delete-${stamp}`, 'Failed to delete tasks.',
          (rowId) => {
            const t = removed.find((r) => r.id === rowId)
            if (t) set((s) => (s.tasks.some((x) => x.id === rowId) ? {} : { tasks: [...s.tasks, t] }))
          })
      },

      bulkSetPriority: (ids, priority) => {
        get()._snapshot()
        set((s) => ({ tasks: s.tasks.map((t) => ids.includes(t.id) ? { ...t, priority } : t) }))
        if (!supabase || !ids.length) return
        const stamp = Date.now()
        get()._bulkRunWrites(ids, 'tasks', 'update', { priority }, `bulk-priority-${stamp}`, 'Failed to update task priority.')
      },

      bulkAssignToMe: (ids) => {
        get()._snapshot()
        const uid = get().currentUser?.id
        if (!uid) return
        set((s) => ({ tasks: s.tasks.map((t) => ids.includes(t.id) ? { ...t, assigneeId: uid } : t) }))
        if (!supabase || !ids.length) return
        const stamp = Date.now()
        get()._bulkRunWrites(ids, 'tasks', 'update', { assignee_id: uid }, `bulk-assign-${stamp}`, 'Failed to assign tasks.')
      },

      bulkSetDueDate: (ids, dueDate) => {
        get()._snapshot()
        const updates = []
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (!ids.includes(t.id)) return t
            if (t.dueDate === dueDate) return t
            updates.push(t.id)
            return { ...t, dueDate }
          })
        }))
        if (!supabase || !updates.length) return
        const stamp = Date.now()
        get()._bulkRunWrites(updates, 'tasks', 'update', { due_date: dueDate }, `bulk-due-${stamp}`, 'Failed to set task dates.')
      },

      bulkShiftDates: (ids, days) => {
        get()._snapshot()
        const updates = [] // [{ id, due_date }]
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (!ids.includes(t.id) || !t.dueDate) return t
            const d = new Date(t.dueDate + 'T00:00:00')
            d.setDate(d.getDate() + days)
            const newDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            updates.push({ id: t.id, due_date: newDate })
            return { ...t, dueDate: newDate }
          })
        }))
        if (!supabase || !updates.length) return
        const stamp = Date.now()
        // Each row has a different due_date — fire individually rather than via _bulkRunWrites.
        get()._writeStart()
        let remaining = updates.length
        let permanentFailures = 0
        updates.forEach(({ id, due_date }) => {
          retryWrite({
            id: `bulk-shift-${stamp}-${id}`,
            table: 'tasks',
            op: 'update',
            filter: { eq: { id } },
            data: { due_date },
          }, supabase, {
            onOk: () => { remaining--; if (remaining === 0) { permanentFailures ? get()._writeFail(`Failed to shift ${permanentFailures}/${updates.length} task date(s).`) : get()._writeOk() } },
            onBlocked: () => { permanentFailures++; remaining--; if (remaining === 0) { get()._writeFail(`Some date shifts blocked by permissions (${permanentFailures}/${updates.length}).`) } },
            onFail: (_e, { permanent } = {}) => {
              if (permanent) permanentFailures++
              remaining--
              if (remaining === 0) {
                if (permanentFailures) get()._writeFail(`Failed to shift ${permanentFailures}/${updates.length} task date(s).`)
                else get()._writeOk()
              }
            },
          })
        })
      },

      // Internal helper — fires one retryWrite per id with shared data. Aggregates outcome
      // into a single _writeOk / _writeFail call so the spinner doesn't dance.
      // onRowPermanentFail(rowId) — optional. Called when a single row's write is
      // permanently rejected (RLS block or fatal DB error) so the caller can roll back
      // that row's optimistic mutation. NOT called on transient failures (those stay
      // queued and will retry — rolling back would lose the change).
      _bulkRunWrites: (ids, table, op, data, idPrefix, failMessage, onRowPermanentFail) => {
        get()._writeStart()
        let remaining = ids.length
        let permanentFailures = 0
        ids.forEach((rowId) => {
          retryWrite({
            id: `${idPrefix}-${rowId}`,
            table,
            op,
            filter: { eq: { id: rowId } },
            ...(data ? { data } : {}),
          }, supabase, {
            onOk: () => { remaining--; if (remaining === 0) { permanentFailures ? get()._writeFail(`${failMessage} (${permanentFailures}/${ids.length} failed)`) : get()._writeOk() } },
            onBlocked: () => { permanentFailures++; onRowPermanentFail?.(rowId); remaining--; if (remaining === 0) get()._writeFail(`${failMessage} Permission denied for ${permanentFailures}/${ids.length}.`) },
            onFail: (_e, { permanent } = {}) => {
              if (permanent) { permanentFailures++; onRowPermanentFail?.(rowId) }
              remaining--
              if (remaining === 0) {
                if (permanentFailures) get()._writeFail(`${failMessage} (${permanentFailures}/${ids.length} failed)`)
                else get()._writeOk()
              }
            },
          })
        })
      },

      // ── Sections ──────────────────────────────────────────────────────────────
      // Durably persist an auto-created system section (Approval/Complete/Misc).
      // These are background self-heal inserts (no spinner), but they must survive a
      // reload/outage — the old fire-and-forget insert could drop the row, leaving a
      // section visible in the UI that vanished on the next load. keepOnFail keeps it
      // queued for reconnect replay; the optimistic row is rolled back only on a
      // permanent failure (RLS block / fatal DB error), never on a transient one.
      _persistAutoSection: (sec) => {
        if (!supabase) return
        retryWrite({
          id: `auto-section-${sec.id}`,
          table: 'sections',
          op: 'insert',
          data: { id: sec.id, project_id: sec.projectId, name: sec.name, order: sec.order, type: sec.type },
          select: 'id',
          keepOnFail: true,
        }, supabase, {
          onBlocked: () => set((s) => ({ sections: s.sections.filter((ex) => ex.id !== sec.id) })),
          onFail: (e, { permanent } = {}) => {
            if (permanent) { console.error('[autoSection] insert failed:', e?.code, e?.message); set((s) => ({ sections: s.sections.filter((ex) => ex.id !== sec.id) })) }
          },
        })
      },

      addSection: (projectId, name, type = null, extra = {}) => {
        const id = supabase ? crypto.randomUUID() : 'sec' + Date.now()
        const order = get().sections.filter((s) => s.projectId === projectId).length
        if (type) {
          try { const st = JSON.parse(localStorage.getItem('prjct-section-types') || '{}'); st[id] = type; localStorage.setItem('prjct-section-types', JSON.stringify(st)) } catch {}
        }
        const { deliverableType = null, deliverableQuota = null, silent = false } = extra
        set((s) => ({ sections: [...s.sections, { id, projectId, name, order, type, deliverableType, deliverableQuota }] }))
        if (supabase) {
          if (!silent) get()._writeStart()
          const row = { id, project_id: projectId, name, order, ...(type ? { type } : {}), ...(deliverableType ? { deliverable_type: deliverableType } : {}), ...(deliverableQuota ? { deliverable_quota: deliverableQuota } : {}) }
          retryWrite({
            id: `add-section-${id}`,
            table: 'sections',
            op: 'insert',
            data: row,
            select: 'id',
            keepOnFail: true,
          }, supabase, {
            onOk: () => { if (!silent) get()._writeOk() },
            onBlocked: () => {
              set((s) => ({ sections: s.sections.filter((sec) => sec.id !== id) }))
              if (!silent) get()._writeFail('Section not saved — permission denied.', true, { table: 'sections', op: 'insert', entityId: id, entityLabel: name })
            },
            onFail: (e, { permanent } = {}) => {
              if (permanent) {
                set((s) => ({ sections: s.sections.filter((sec) => sec.id !== id) }))
                if (!silent) get()._writeFail(`Failed to create section "${name}".`, true, { table: 'sections', op: 'insert', entityId: id, entityLabel: name, code: e?.code })
              } else {
                if (!silent) get()._writeFail(`Connection issue — section "${name}" saved locally and will sync when connection is restored.`, false, { table: 'sections', op: 'insert', entityId: id, entityLabel: name, code: e?.code })
              }
            },
          })
        }
        return id
      },

      updateSection: (id, updates) => {
        const prevSection = get().sections.find((sec) => sec.id === id)
        const restore = () => { if (prevSection) set((s) => ({ sections: s.sections.map((sec) => sec.id === id ? prevSection : sec) })) }
        set((s) => ({ sections: s.sections.map((sec) => sec.id === id ? { ...sec, ...updates } : sec) }))
        if (!supabase) return
        const db = {}
        if (updates.name !== undefined) db.name = updates.name
        if (updates.order !== undefined) db.order = updates.order
        if (updates.type !== undefined) db.type = updates.type
        if (updates.approvers !== undefined) db.approvers = updates.approvers
        if (updates.deliverableType !== undefined) db.deliverable_type = updates.deliverableType
        if (updates.deliverableQuota !== undefined) db.deliverable_quota = updates.deliverableQuota
        if (Object.keys(db).length) {
          get()._writeStart()
          retryWrite({
            id: `update-section-${id}-${Date.now()}`,
            table: 'sections',
            op: 'update',
            filter: { eq: { id } },
            data: db,
            select: 'id',
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => { restore(); get()._writeFail('Section update blocked — permission denied.', true, { table: 'sections', op: 'update', entityId: id }) },
            onFail: (e, { permanent } = {}) => {
              if (permanent) restore()
              get()._writeFail(
                permanent ? 'Failed to update section.' : 'Connection issue — section update will sync when connection is restored.',
                permanent,
                { table: 'sections', op: 'update', entityId: id, code: e?.code }
              )
            },
          })
        }
      },

      deleteSection: (id) => {
        get()._snapshot()
        // Capture section + its tasks so a permanently-rejected delete can be restored.
        const removedSection = get().sections.find((sec) => sec.id === id)
        const removedTasks = get().tasks.filter((t) => t.sectionId === id)
        const restore = () => set((s) => ({
          sections: removedSection && !s.sections.some((x) => x.id === id) ? [...s.sections, removedSection] : s.sections,
          tasks: [...s.tasks, ...removedTasks.filter((rt) => !s.tasks.some((x) => x.id === rt.id))],
        }))
        // Cancel pending INSERTs for tasks in this section before wiping them
        const sectionTaskIds = removedTasks.map((t) => t.id)
        cancelQueuedInserts('tasks', sectionTaskIds)
        set((s) => ({
          sections: s.sections.filter((sec) => sec.id !== id),
          tasks: s.tasks.filter((t) => t.sectionId !== id),
          _pendingSyncIds: s._pendingSyncIds.filter((pid) => !sectionTaskIds.includes(pid)),
        }))
        if (supabase) {
          get()._writeStart()
          retryWrite({
            id: `delete-section-${id}-${Date.now()}`,
            table: 'sections',
            op: 'delete',
            filter: { eq: { id } },
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => { restore(); get()._writeFail('Section delete blocked — permission denied.', true, { table: 'sections', op: 'delete', entityId: id }) },
            onFail: (e, { permanent } = {}) => {
              if (permanent) restore()
              get()._writeFail(
                permanent ? 'Failed to delete section.' : 'Connection issue — section delete will sync when connection is restored.',
                permanent,
                { table: 'sections', op: 'delete', entityId: id, code: e?.code }
              )
            },
          })
        }
      },

      // ── Projects ──────────────────────────────────────────────────────────────
      // Promise wrapper so retryWrite outcomes can be awaited. Resolves on the first
      // terminal callback (onOk / onBlocked / onFail). Used by flows where FK ordering
      // matters — e.g. project_members upsert must wait for the project insert to land.
      _awaitWrite: (descriptor) => new Promise((resolve) => {
        retryWrite(descriptor, supabase, {
          onOk: () => resolve({ ok: true }),
          onBlocked: () => resolve({ ok: false, blocked: true }),
          onFail: (err, { permanent } = {}) => resolve({ ok: false, err, permanent: !!permanent }),
        })
      }),

      addProject: async (data) => {
        const id = supabase ? crypto.randomUUID() : 'p' + Date.now()
        const userId = get().currentUser?.id
        const parentId = data.parentId || null
        const project = { id, name: data.name || 'Untitled', color: data.color || '#e63946', icon: data.icon || '◈', description: data.description || '', privacy: data.privacy || 'private', completed: false, ownerId: userId, parentId }
        set((s) => ({ projects: [...s.projects, project] }))
        if (supabase) {
          get()._writeStart()
          const meta = { table: 'projects', op: 'insert', entityId: id, entityLabel: project.name }
          const result = await get()._awaitWrite({
            id: `add-project-${id}`,
            table: 'projects',
            op: 'insert',
            keepOnFail: true,
            select: 'id',
            data: { id, name: project.name, color: project.color, icon: project.icon, description: project.description, privacy: project.privacy, owner_id: userId, completed: false, ...(parentId ? { parent_id: parentId } : {}) },
          })
          if (!result.ok) {
            if (result.blocked || result.permanent) {
              set((s) => ({ projects: s.projects.filter((p) => p.id !== id) }))
              get()._writeFail(
                result.blocked ? `Couldn't create "${project.name}" — permission denied.` : `Failed to create project "${project.name}".`,
                true,
                { ...meta, code: result.err?.code }
              )
              return null
            }
            // Transient (timeout) — leave optimistic state in place, write is queued for replay.
            // Skip member upsert (FK would 23503 since project insert hasn't landed yet);
            // loadUserData self-heal will repair the project_members row on next reconnect.
            get()._writeFail(`Connection issue — project "${project.name}" saved locally and will sync when connection is restored.`, false, { ...meta, code: result.err?.code })
            return id
          }
          get()._writeOk()
          // Owner-as-member upsert. The add_owner_as_member trigger usually handles this
          // server-side, but we also queue it client-side so a missing trigger can't
          // silently break the project_members gating contract from CLAUDE.md (#7).
          retryWrite({
            id: `add-project-member-${id}`,
            table: 'project_members',
            op: 'upsert',
            keepOnFail: true,
            upsertOptions: { onConflict: 'project_id,user_id' },
            data: { project_id: id, user_id: userId, role: 'owner' },
          }, supabase, {
            onFail: (e) => console.warn('[addProject members] queued for retry:', e?.message),
          })
          await get().loadUserData(userId)
        }
        return id
      },

      createClientBoard: async (parentId, data) => {
        const id = await get().addProject({ ...data, parentId })
        if (id && parentId && supabase) {
          // Mark parent as workspace — non-critical, queue and continue.
          retryWrite({
            id: `mark-workspace-${parentId}`,
            table: 'projects',
            op: 'update',
            filter: { eq: { id: parentId } },
            data: { is_workspace: true },
          }, supabase, {
            onOk: () => get().updateProject(parentId, { isWorkspace: true }),
            onFail: (e) => console.warn('[createClientBoard] mark workspace queued:', e?.message),
          })
          // Add all existing workspace members to the new child board
          const { data: workspaceMembers } = await supabase
            .from('project_members')
            .select('user_id, permissions')
            .eq('project_id', parentId)
            .neq('user_id', get().currentUser?.id) // owner already added by addProject
          if (workspaceMembers?.length) {
            retryWrite({
              id: `client-board-members-${id}`,
              table: 'project_members',
              op: 'upsert',
              keepOnFail: true,
              upsertOptions: { onConflict: 'project_id,user_id' },
              data: workspaceMembers.map((m) => ({ project_id: id, user_id: m.user_id, role: 'member', permissions: m.permissions })),
            }, supabase, {
              onFail: (e) => console.warn('[createClientBoard] member upsert queued:', e?.message),
            })
          }
        }
        return id
      },

      updateProject: (id, updates) => {
        const prev = get().projects.find((p) => p.id === id)
        const prevName = prev?.name
        const restore = () => { if (prev) set((s) => ({ projects: s.projects.map((p) => p.id === id ? prev : p) })) }
        set((s) => ({ projects: s.projects.map((p) => p.id === id ? { ...p, ...updates } : p) }))
        if (!supabase) return
        const db = {}
        if (updates.name !== undefined) db.name = updates.name
        if (updates.color !== undefined) db.color = updates.color
        if (updates.icon !== undefined) db.icon = updates.icon
        if (updates.description !== undefined) db.description = updates.description
        if (updates.completed !== undefined) db.completed = updates.completed
        if (updates.views !== undefined) db.views = updates.views
        if (updates.brandKit !== undefined) db.brand_kit = updates.brandKit
        if (updates.embeds !== undefined) db.embeds = updates.embeds
        if (updates.isWorkspace !== undefined) db.is_workspace = updates.isWorkspace
        if (Object.keys(db).length) {
          const label = updates.name || prevName
          const meta = { table: 'projects', op: 'update', entityId: id, entityLabel: label }
          get()._writeStart()
          retryWrite({
            id: `update-project-${id}-${Date.now()}`,
            table: 'projects',
            op: 'update',
            filter: { eq: { id } },
            data: db,
            select: 'id',
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => { restore(); get()._writeFail(label ? `Couldn't save "${label}" — permission denied.` : 'Project update not saved — permission denied.', true, meta) },
            onFail: (e, { permanent } = {}) => {
              if (permanent) restore()
              get()._writeFail(
                permanent ? (label ? `Failed to save "${label}".` : 'Failed to save project changes.') : `Connection issue — ${label || 'project'} update will sync when connection is restored.`,
                permanent,
                { ...meta, code: e?.code }
              )
            },
          })
        }
      },

      deleteProject: (id) => {
        get()._snapshot()
        // Capture project + its sections + their tasks + the active-project pointer so a
        // permanently-rejected delete restores the full subtree, not a half-wiped board.
        const removedProject = get().projects.find((p) => p.id === id)
        const removedSections = get().sections.filter((s) => s.projectId === id)
        const sectionIds = removedSections.map((s) => s.id)
        const removedTasks = get().tasks.filter((t) => sectionIds.includes(t.sectionId))
        const prevActive = get().activeProjectId
        const restore = () => set((s) => ({
          projects: removedProject && !s.projects.some((p) => p.id === id) ? [...s.projects, removedProject] : s.projects,
          sections: [...s.sections, ...removedSections.filter((rs) => !s.sections.some((x) => x.id === rs.id))],
          tasks: [...s.tasks, ...removedTasks.filter((rt) => !s.tasks.some((x) => x.id === rt.id))],
          activeProjectId: prevActive,
        }))
        set((s) => ({
          projects: s.projects.filter((p) => p.id !== id),
          sections: s.sections.filter((sec) => sec.projectId !== id),
          tasks: s.tasks.filter((t) => !sectionIds.includes(t.sectionId)),
          activeProjectId: s.activeProjectId === id ? (s.projects.filter((p) => p.id !== id)[0]?.id || null) : s.activeProjectId,
        }))
        if (supabase) {
          get()._writeStart()
          retryWrite({
            id: `delete-project-${id}-${Date.now()}`,
            table: 'projects',
            op: 'delete',
            filter: { eq: { id } },
            select: 'id',
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => { restore(); get()._writeFail('Project delete blocked — permission denied.', true, { table: 'projects', op: 'delete', entityId: id }) },
            onFail: (e, { permanent } = {}) => {
              if (permanent) restore()
              get()._writeFail(
                permanent ? 'Failed to delete project.' : 'Connection issue — project delete will sync when connection is restored.',
                permanent,
                { table: 'projects', op: 'delete', entityId: id, code: e?.code }
              )
            },
          })
        }
      },

      duplicateProject: (id) => {
        const { projects, sections, tasks } = get()
        const project = projects.find((p) => p.id === id)
        if (!project) return null
        const userId = get().currentUser?.id
        const newProjectId = supabase ? crypto.randomUUID() : 'p' + Date.now()
        const newProject = { ...project, id: newProjectId, name: project.name + ' (Copy)', completed: false, ownerId: userId }
        const sectionIdMap = {}
        const newSections = sections
          .filter((s) => s.projectId === id)
          .map((s) => {
            const newId = supabase ? crypto.randomUUID() : 'sec' + Date.now() + Math.random()
            sectionIdMap[s.id] = newId
            return { ...s, id: newId, projectId: newProjectId }
          })
        const newTasks = tasks
          .filter((t) => t.projectId === id && !t.parentId)
          .map((t) => ({
            ...t,
            id: supabase ? crypto.randomUUID() : 't' + Date.now() + Math.random(),
            projectId: newProjectId,
            sectionId: sectionIdMap[t.sectionId] || t.sectionId,
            comments: [],
            subtasks: (t.subtasks || []).map((st) => ({ ...st, id: crypto.randomUUID() })),
          }))
        set((s) => ({
          projects: [...s.projects, newProject],
          sections: [...s.sections, ...newSections],
          tasks: [...s.tasks, ...newTasks],
        }))
        if (supabase) {
          get()._writeStart()
          ;(async () => {
            const { error: pe } = await supabase.from('projects').insert({ id: newProjectId, name: newProject.name, color: newProject.color, icon: newProject.icon, description: newProject.description, privacy: newProject.privacy, owner_id: userId, completed: false })
            if (pe) { console.error('duplicateProject:', pe); get()._writeFail('Failed to duplicate project.'); return }
            const { error: me } = await supabase.from('project_members').upsert(
              { project_id: newProjectId, user_id: userId, role: 'owner' },
              { onConflict: 'project_id,user_id' }
            )
            if (me) { console.error('duplicateProject members:', me); get()._writeFail('Failed to duplicate project.'); return }
            for (const s of newSections) {
              const { error: se } = await supabase.from('sections').insert({ id: s.id, project_id: newProjectId, name: s.name, type: s.type, order: s.order, deliverable_type: s.deliverableType || null, deliverable_quota: s.deliverableQuota || null })
              if (se) { console.error('duplicateProject section:', se); get()._writeFail('Failed to duplicate project sections.'); return }
            }
            for (const t of newTasks) {
              const { error: te } = await supabase.from('tasks').insert({ id: t.id, title: t.title, description: t.description, project_id: newProjectId, section_id: t.sectionId, assignee_id: t.assigneeId, due_date: t.dueDate, start_date: t.startDate, priority: t.priority, tags: t.tags, subtasks: t.subtasks, comments: [], attachments: t.attachments, created_by: userId, completed: false, deliverable_type: t.deliverableType || null, recurrence: t.recurrence || null })
              if (te) { console.error('duplicateProject task:', te); get()._writeFail('Failed to duplicate project tasks.'); return }
            }
            get()._writeOk()
          })().catch((e) => { console.error('duplicateProject catch:', e); get()._writeFail('Failed to duplicate project.') })
        }
        return newProjectId
      },

      reorderProjects: (parentId, orderedIds) => {
        const key = parentId || 'top'
        set((s) => ({ _projectOrder: { ...s._projectOrder, [key]: orderedIds } }))
        try {
          const stored = JSON.parse(localStorage.getItem('prjct-project-order') || '{}')
          stored[key] = orderedIds
          localStorage.setItem('prjct-project-order', JSON.stringify(stored))
        } catch {}
      },

      archiveProject: (id) => {
        get()._snapshot()
        set((s) => ({ projects: s.projects.map((p) => p.id === id ? { ...p, completed: true } : p) }))
        if (supabase) retryWrite({
          id: `archive-project-${id}-${Date.now()}`,
          table: 'projects',
          op: 'update',
          filter: { eq: { id } },
          data: { completed: true },
        }, supabase, {
          onFail: (e, { permanent } = {}) => {
            if (permanent) window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Failed to archive project.' } }))
            else console.warn('[archiveProject] queued for retry:', e?.message)
          },
        })
      },

      unarchiveProject: (id) => {
        get()._snapshot()
        set((s) => ({ projects: s.projects.map((p) => p.id === id ? { ...p, completed: false } : p) }))
        if (supabase) retryWrite({
          id: `unarchive-project-${id}-${Date.now()}`,
          table: 'projects',
          op: 'update',
          filter: { eq: { id } },
          data: { completed: false },
        }, supabase, {
          onFail: (e, { permanent } = {}) => {
            if (permanent) window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Failed to unarchive project.' } }))
            else console.warn('[unarchiveProject] queued for retry:', e?.message)
          },
        })
      },

      exportTasksCSV: (projectId) => {
        const { tasks, projects, sections } = get()
        const rows = (projectId ? tasks.filter((t) => t.projectId === projectId) : tasks)
          .filter((t) => !t.parentId)
          .map((t) => {
            const proj = projects.find((p) => p.id === t.projectId)
            const sec  = sections.find((s) => s.id === t.sectionId)
            return [
              `"${(t.title || '').replace(/"/g, '""')}"`,
              `"${proj?.name || ''}"`,
              `"${sec?.name || ''}"`,
              `"${t.priority || 'none'}"`,
              `"${t.dueDate || ''}"`,
              `"${t.completed ? 'done' : 'open'}"`,
            ].join(',')
          })
        const csv = ['Title,Project,Section,Priority,Due Date,Status', ...rows].join('\n')
        const blob = new Blob([csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `prjct-tasks-${new Date().toISOString().split('T')[0]}.csv`
        a.click()
        URL.revokeObjectURL(url)
      },

      // ── Goals ─────────────────────────────────────────────────────────────────
      addGoal: (data) => {
        const id = supabase ? crypto.randomUUID() : 'g' + Date.now()
        const goal = { id, progress: 0, status: 'on_track', createdAt: new Date().toISOString(), ...data }
        set((s) => ({ goals: [...s.goals, goal] }))
        if (supabase) {
          get()._writeStart()
          retryWrite({
            id: `add-goal-${id}`,
            table: 'goals',
            op: 'insert',
            keepOnFail: true,
            select: 'id',
            data: { id, title: goal.title, description: goal.description || '', progress: 0, status: 'on_track', due_date: goal.dueDate || null, owner_id: get().currentUser?.id },
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => {
              set((s) => ({ goals: s.goals.filter((g) => g.id !== id) }))
              get()._writeFail('Goal creation blocked — permission denied.', true, { table: 'goals', op: 'insert', entityId: id, entityLabel: goal.title })
            },
            onFail: (e, { permanent } = {}) => {
              if (permanent) {
                set((s) => ({ goals: s.goals.filter((g) => g.id !== id) }))
                get()._writeFail(`Failed to create goal "${goal.title}".`, true, { table: 'goals', op: 'insert', entityId: id, entityLabel: goal.title, code: e?.code })
              } else {
                get()._writeFail(`Connection issue — goal "${goal.title}" saved locally and will sync when connection is restored.`, false, { table: 'goals', op: 'insert', entityId: id, entityLabel: goal.title, code: e?.code })
              }
            },
          })
        }
        return id
      },

      updateGoal: (id, updates) => {
        const prevGoal = get().goals.find((g) => g.id === id)
        const restore = () => { if (prevGoal) set((s) => ({ goals: s.goals.map((g) => g.id === id ? prevGoal : g) })) }
        set((s) => ({ goals: s.goals.map((g) => g.id === id ? { ...g, ...updates } : g) }))
        if (!supabase) return
        const db = {}
        if (updates.title !== undefined) db.title = updates.title
        if (updates.description !== undefined) db.description = updates.description
        if (updates.progress !== undefined) db.progress = updates.progress
        if (updates.status !== undefined) db.status = updates.status
        if (updates.dueDate !== undefined) db.due_date = updates.dueDate
        if (Object.keys(db).length) {
          get()._writeStart()
          retryWrite({
            id: `update-goal-${id}-${Date.now()}`,
            table: 'goals',
            op: 'update',
            filter: { eq: { id } },
            data: db,
            select: 'id',
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => { restore(); get()._writeFail('Goal update blocked — permission denied.', true, { table: 'goals', op: 'update', entityId: id }) },
            onFail: (e, { permanent } = {}) => {
              if (permanent) restore()
              get()._writeFail(
                permanent ? 'Failed to update goal.' : 'Connection issue — goal update will sync when connection is restored.',
                permanent,
                { table: 'goals', op: 'update', entityId: id, code: e?.code }
              )
            },
          })
        }
      },

      deleteGoal: (id) => {
        const removedGoal = get().goals.find((g) => g.id === id)
        const restore = () => { if (removedGoal) set((s) => (s.goals.some((g) => g.id === id) ? {} : { goals: [...s.goals, removedGoal] })) }
        set((s) => ({ goals: s.goals.filter((g) => g.id !== id) }))
        if (supabase) {
          get()._writeStart()
          retryWrite({
            id: `delete-goal-${id}-${Date.now()}`,
            table: 'goals',
            op: 'delete',
            filter: { eq: { id } },
            select: 'id',
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => { restore(); get()._writeFail('Goal delete blocked — permission denied.', true, { table: 'goals', op: 'delete', entityId: id }) },
            onFail: (e, { permanent } = {}) => {
              if (permanent) restore()
              get()._writeFail(
                permanent ? 'Failed to delete goal.' : 'Connection issue — goal delete will sync when connection is restored.',
                permanent,
                { table: 'goals', op: 'delete', entityId: id, code: e?.code }
              )
            },
          })
        }
      },

      // ── Collaboration ─────────────────────────────────────────────────────────
      inviteToProject: async (projectId, email, role = 'member') => {
        if (!supabase) return { error: { message: 'Supabase not configured' } }
        const token = crypto.randomUUID()
        const inviteId = crypto.randomUUID()
        const result = await get()._awaitWrite({
          id: `invite-${inviteId}`,
          table: 'project_invitations',
          op: 'insert',
          keepOnFail: true,
          data: { id: inviteId, project_id: projectId, email, invited_by: get().currentUser?.id, role, token },
        })
        if (!result.ok && (result.blocked || result.permanent)) {
          return { error: result.err || { message: 'Failed to create invitation' } }
        }
        // result.ok OR transient (queued for retry) — proceed to email anyway so user gets the link.
        // The recipient won't be able to accept until the invite row lands, but the link is safe to share.
        const inviteLink = `${window.location.origin}/invite/${token}`
        const { currentUser, projects } = get()
        const project = projects.find((p) => p.id === projectId)
        fetch('/api/send-invite', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, inviteLink, projectName: project?.name, inviterName: currentUser?.name }),
        }).catch((e) => console.warn('send-invite email failed:', e))
        return { inviteLink }
      },

      acceptInvite: async (token) => {
        if (!supabase) return { error: { message: 'Supabase not configured' } }
        // Look up invitation directly to avoid ambiguous accept_invitation() RPC overloads
        const { data: invite, error: fetchErr } = await supabase
          .from('project_invitations')
          .select('id, project_id, role, status, email')
          .eq('token', token)
          .maybeSingle()
        if (fetchErr || !invite) return { error: { message: 'Invalid or expired invite link' } }
        if (invite.status === 'accepted') return { error: { message: 'This invite has already been used' } }
        const userId = get().currentUser?.id
        if (!userId) return { error: { message: 'You must be signed in to accept an invite' } }
        // Member upsert MUST succeed before marking invitation accepted — otherwise the row
        // is "accepted" with no membership. keepOnFail so a transient timeout doesn't lose it.
        const memberResult = await get()._awaitWrite({
          id: `accept-invite-member-${invite.id}`,
          table: 'project_members',
          op: 'upsert',
          keepOnFail: true,
          upsertOptions: { onConflict: 'project_id,user_id' },
          data: { project_id: invite.project_id, user_id: userId, role: invite.role },
        })
        if (!memberResult.ok && (memberResult.blocked || memberResult.permanent)) {
          return { error: memberResult.err || { message: 'Failed to join project' } }
        }
        // Mark invitation accepted (queued so it survives a connection blip)
        retryWrite({
          id: `accept-invite-mark-${invite.id}`,
          table: 'project_invitations',
          op: 'update',
          filter: { eq: { id: invite.id } },
          data: { status: 'accepted' },
        }, supabase, {
          onFail: (e) => console.warn('[acceptInvite] mark accepted queued for retry:', e?.message),
        })
        // If joining a workspace, also add to all existing child boards
        const { data: invitedProject } = await supabase.from('projects').select('is_workspace').eq('id', invite.project_id).maybeSingle()
        if (invitedProject?.is_workspace) {
          const { data: childBoards } = await supabase.from('projects').select('id').eq('parent_id', invite.project_id).eq('completed', false)
          if (childBoards?.length) {
            retryWrite({
              id: `accept-invite-children-${invite.id}`,
              table: 'project_members',
              op: 'upsert',
              keepOnFail: true,
              upsertOptions: { onConflict: 'project_id,user_id' },
              data: childBoards.map((c) => ({ project_id: c.id, user_id: userId, role: 'member' })),
            }, supabase, {
              onFail: (e) => console.warn('[acceptInvite] child boards upsert queued:', e?.message),
            })
          }
        }
        await get().loadUserData(userId)
        return { projectId: invite.project_id }
      },

      getProjectMembers: async (projectId, { force = false } = {}) => {
        if (!supabase) return []
        const cache = get()._membersCache
        if (!force && cache[projectId]) return cache[projectId]

        // For child boards, also pull workspace (parent) members so assignee picker is complete
        const project = get().projects.find((p) => p.id === projectId)
        const parentId = project?.parentId || null

        const queries = [
          supabase.from('project_members').select('user_id, role, permissions').eq('project_id', projectId),
          ...(parentId ? [supabase.from('project_members').select('user_id, role, permissions').eq('project_id', parentId)] : []),
        ]
        const results = await Promise.all(queries)
        if (results[0].error) {
          console.error('[getProjectMembers] project_members query failed — code:', results[0].error.code, '| message:', results[0].error.message, '| hint:', results[0].error.hint)
          return []
        }

        // Merge rows, child board membership takes priority over parent membership
        const seenIds = new Set()
        const memberRows = []
        for (const res of results) {
          for (const row of (res.data || [])) {
            if (!seenIds.has(row.user_id)) { seenIds.add(row.user_id); memberRows.push(row) }
          }
        }

        const { currentUser } = get()

        if (!memberRows.length) {
          console.warn('[getProjectMembers] no rows for project', projectId, '— check project_members table RLS or missing record')
          // Still return current user so UI never shows completely empty
          if (currentUser) {
            const fallback = [{ userId: currentUser.id, name: currentUser.name || 'Me', email: currentUser.email || '', avatar: currentUser.avatar || null, role: project?.ownerId === currentUser.id ? 'owner' : 'member', permissions: { create_tasks: true, edit_tasks: true, delete_tasks: true, manage_sections: true, invite_members: false, approve_tasks: false } }]
            set((s) => ({ _membersCache: { ...s._membersCache, [projectId]: fallback } }))
            return fallback
          }
          return []
        }

        const userIds = memberRows.map((m) => m.user_id)
        const { data: profiles, error: profErr } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url')
          .in('id', userIds)
        if (profErr) console.warn('[getProjectMembers] profiles query failed:', profErr.message)
        const profileMap = {}
        ;(profiles || []).forEach((p) => { profileMap[p.id] = p })

        const members = memberRows.map((m) => {
          const p = profileMap[m.user_id]
          const name = p?.name || (m.user_id === currentUser?.id ? currentUser?.name : null) || 'Unknown'
          const email = p?.email || (m.user_id === currentUser?.id ? currentUser?.email : '') || ''
          const avatar = p?.avatar_url || (m.user_id === currentUser?.id ? currentUser?.avatar : null) || null
          return {
            userId: m.user_id,
            role: m.role,
            permissions: m.permissions || { create_tasks: true, edit_tasks: true, delete_tasks: true, manage_sections: true, invite_members: false, approve_tasks: false },
            name,
            email,
            avatar,
          }
        })
        // Always include current user — project_members row may be missing or profile query may fail
        if (currentUser && !members.some((m) => m.userId === currentUser.id)) {
          members.unshift({ userId: currentUser.id, name: currentUser.name || 'Me', email: currentUser.email || '', avatar: currentUser.avatar || null, role: project?.ownerId === currentUser.id ? 'owner' : 'member', permissions: { create_tasks: true, edit_tasks: true, delete_tasks: true, manage_sections: true, invite_members: false, approve_tasks: false } })
        }
        set((s) => ({ _membersCache: { ...s._membersCache, [projectId]: members } }))
        return members
      },
      invalidateMembersCache: (projectId) => set((s) => {
        const c = { ...s._membersCache }
        if (projectId) delete c[projectId]; else return { _membersCache: {} }
        return { _membersCache: c }
      }),

      // Self-healing: call from components that read _membersCache — triggers background fetch if empty
      _ensurePending: new Set(),
      ensureMembers: (projectId) => {
        if (!projectId || !supabase) return
        const cache = get()._membersCache
        if (cache[projectId]?.length) return // already populated
        if (get()._ensurePending.has(projectId)) return // fetch already in-flight
        get()._ensurePending.add(projectId)
        get().getProjectMembers(projectId).finally(() => { get()._ensurePending.delete(projectId) })
      },

      canDo: (projectId, permission) => {
        const { projects, currentUser } = get()
        const project = projects.find((p) => p.id === projectId)
        if (!project) return false
        if (project.ownerId === currentUser?.id || project.role === 'owner') return true
        // Workspace ownership cascades: owner of the parent workspace gets owner privileges on the child.
        const workspace = project.parentId ? projects.find((p) => p.id === project.parentId) : null
        if (workspace?.role === 'owner') return true
        return project.myPermissions?.[permission] === true
      },

      updateMemberPermissions: async (projectId, userId, permissions) => {
        if (!supabase) return
        const { error } = await supabase
          .from('project_members')
          .update({ permissions })
          .eq('project_id', projectId)
          .eq('user_id', userId)
        if (error) { console.error('updateMemberPermissions:', error); window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Failed to update member permissions.' } })) }
      },

      updateMemberRole: async (projectId, userId, role) => {
        if (!supabase) return { ok: false, error: new Error('No supabase client') }
        const { error } = await supabase
          .from('project_members')
          .update({ role })
          .eq('project_id', projectId)
          .eq('user_id', userId)
        if (error) {
          console.error('updateMemberRole:', error)
          window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Failed to update member role.' } }))
          return { ok: false, error }
        }
        // If the updated row belongs to the current user, patch projects[].role immediately
        // so role-gated logic (e.g., approval routing) reads fresh data without waiting for realtime.
        const currentUserId = get().currentUser?.id
        if (userId === currentUserId) {
          set((s) => ({ projects: s.projects.map((p) => p.id === projectId ? { ...p, role } : p) }))
        }
        get().invalidateMembersCache(projectId)
        return { ok: true }
      },

      removeMember: async (projectId, userId) => {
        if (!supabase) return
        const isSelf = userId === get().currentUser?.id
        // Mark self-leaves so the realtime handler doesn't show the kicked screen
        if (isSelf) _selfLeftProjects.add(projectId)
        // Use RPC to bypass RLS — direct DELETE was silently blocked despite correct policies
        const { data: success, error } = await supabase.rpc('remove_project_member', {
          p_project_id: projectId,
          p_user_id: userId,
        })
        if (error) {
          if (isSelf) _selfLeftProjects.delete(projectId)
          console.error('removeMember:', error)
          window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Failed to remove member.' } }))
          return
        }
        if (!success) {
          if (isSelf) _selfLeftProjects.delete(projectId)
          console.warn('removeMember: RPC returned false — not authorized or row not found')
          window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Could not remove member — permission denied.' } }))
          return
        }
        // If this is a workspace, also remove from all child boards
        const { projects } = get()
        const isWorkspace = projects.find((p) => p.id === projectId)?.isWorkspace
        if (isWorkspace) {
          const childIds = projects.filter((p) => p.parentId === projectId).map((p) => p.id)
          if (childIds.length) {
            // Durable + retried — a silent failure here left the user removed from the
            // workspace but still a member of its child boards. Surface a permanent
            // failure so it isn't lost.
            const r = await get()._awaitWrite({
              id: `rm-child-members-${projectId}-${userId}-${Date.now()}`,
              table: 'project_members',
              op: 'delete',
              filter: { in: { project_id: childIds }, eq: { user_id: userId } },
              keepOnFail: true,
            })
            if (!r.ok && (r.blocked || r.permanent)) {
              window.dispatchEvent(new CustomEvent('prjct-write-error', { detail: { message: 'Removed from the workspace, but some child boards could not be updated.' } }))
            }
          }
        }
        // If current user left voluntarily, update local state immediately
        if (isSelf) {
          set((s) => ({
            projects: s.projects.filter((pr) => pr.id !== projectId),
            sections: s.sections.filter((sec) => sec.projectId !== projectId),
            tasks: s.tasks.filter((t) => t.projectId !== projectId),
            activeProjectId: s.activeProjectId === projectId ? null : s.activeProjectId,
          }))
        }
      },

      clearKickedFromProject: () => set({ kickedFromProject: null }),

      // ── Auth ──────────────────────────────────────────────────────────────────
      initAuth: async () => {
        if (!supabase) {
          set({ currentUser: MOCK_USER, isAuthenticated: true, workspace: MOCK_WS, projects: MOCK_PROJECTS, sections: MOCK_SECTIONS, tasks: mockTasks('u1'), goals: [], activeProjectId: 'p1', _authReady: true })
          return
        }
        set({ isAuthenticated: false })
        try {
          const { data } = await supabase.auth.getSession()
          if (data?.session) {
            set({ _authToken: data.session.access_token, _authExpiresAt: (data.session.expires_at || 0) * 1000 })
            // Flip _authReady as soon as we know the auth state — _applySession (profile + data fetch)
            // continues in the background. Shell renders immediately with empty arrays; pages
            // populate as data arrives. Saves ~1-5s of loader time on cold boot.
            get()._applySession(data.session.user).catch((err) => console.error('_applySession bg:', err))
          }
        } catch (err) {
          console.error('initAuth failed:', err)
        } finally {
          set({ _authReady: true })
        }

        supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'SIGNED_OUT') {
            set({ _authToken: null, _authExpiresAt: 0 })
            supabase.removeAllChannels()
            set({ isAuthenticated: false, currentUser: null, projects: [], sections: [], tasks: [], goals: [], workspace: null, activeProjectId: null })
          }
          if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session) {
            set({ _authToken: session.access_token, _authExpiresAt: (session.expires_at || 0) * 1000 })
          }
          if (event === 'SIGNED_IN' && session) await get()._applySession(session.user)
          if (event === 'TOKEN_REFRESHED') {
            // Reconnect realtime channels so they pick up the fresh JWT
            const { projects } = get()
            if (projects.length) get()._setupRealtime(projects.map((p) => p.id))
          }
        })

        // Whenever a flush lands queued writes, the queue dequeues them but _pendingSyncIds
        // would otherwise keep their stale IDs forever — leaving tasks visually 'pending'
        // even though they're confirmed on the server. Reconcile against the queue every time
        // a successful sync is announced.
        window.addEventListener('prjct-write-synced', () => {
          get()._reconcilePendingSyncIds()
        })

        // Flush pending writes and refresh whenever the browser regains connectivity.
        // Always reload — realtime does not replay missed events from the offline period,
        // so remote changes from other users would be invisible without a full refresh.
        window.addEventListener('online', async () => {
          const { isAuthenticated, currentUser, loadUserData } = get()
          if (isAuthenticated && currentUser?.id) {
            console.info('[writeQueue] back online — flushing pending writes + refreshing data')
            try {
              const flushed = await flushStalePendingWrites(supabase)
              await loadUserData(currentUser.id)
              if (flushed) window.dispatchEvent(new CustomEvent('prjct-write-synced'))
            } catch {}
          }
        })

        // Re-validate auth session when tab comes back into focus. Browsers throttle JS
        // timers in background tabs, so Supabase's autoRefreshToken timer may not fire —
        // JWT expires silently and the next write/upload fails. Three things must happen
        // for a clean recovery, and ALL three must be guarded:
        //   1) refreshSession — with a hard timeout so a hung refresh can't block the rest
        //   2) realtime.setAuth — explicitly push the new token to the WebSocket, bypassing
        //      the _setupRealtime teardown/rebuild that was TIMED_OUT-looping in logs
        //   3) flush queued writes — user-visible "pending" state clears as soon as possible
        let _hiddenAt = 0
        document.addEventListener('visibilitychange', async () => {
          if (document.visibilityState === 'hidden') {
            _hiddenAt = Date.now()
            return
          }
          const { isAuthenticated, currentUser, _authExpiresAt } = get()
          if (!isAuthenticated || !currentUser?.id) return

          const hiddenMs = _hiddenAt ? Date.now() - _hiddenAt : 0
          const tokenMsLeft = _authExpiresAt - Date.now()
          // Proactive refresh when token is expired or within 2 min of expiry —
          // no longer gated by 2 min hidden, because brief tab-switches can still
          // land on a nearly-expired JWT and break the next write.
          const needsRefresh = tokenMsLeft < 2 * 60 * 1000
          // Silent background refresh after even short idle gaps — realtime can drop missed events
          // during sleep/network blips, and a 30s threshold catches lunch-break tab returns without
          // hammering the DB on quick alt-tabs. Loader is never shown for this path.
          const needsDataReload = hiddenMs > 30 * 1000
          if (!needsRefresh && !needsDataReload) return

          console.info(`[auth] tab visible (hidden ${Math.round(hiddenMs / 1000)}s, token ${Math.round(tokenMsLeft / 1000)}s left)`)

          let refreshedToken = null
          if (needsRefresh) {
            try {
              const refreshed = await Promise.race([
                supabase.auth.refreshSession(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('refreshSession timed out')), 6000)),
              ])
              refreshedToken = refreshed?.data?.session?.access_token || null
            } catch (err) {
              console.warn('[auth] visibility refresh failed:', err?.message)
            }
          }
          // ALWAYS re-bind the current token to the realtime socket on visibility return.
          // Even when the JWT still has plenty of time left, the websocket can drift into
          // a half-broken phoenix state during long idles (browser throttling, system sleep).
          // Re-pushing the token forces the socket to validate, exposing dead connections
          // immediately rather than letting writes queue against a zombie channel.
          try {
            const tokenToPush = refreshedToken || get()._authToken
            if (tokenToPush) supabase.realtime.setAuth(tokenToPush)
          } catch {}

          // Ping before flush — if the connection is genuinely down, blindly flushing N
          // queued writes just produces N timeouts and leaves them queued anyway.
          // Skip and let the next visibility / online event try again.
          let canFlush = true
          try {
            const ping = await get().pingHealth()
            if (!ping.ok) {
              console.info(`[auth] skipping flush, ping failed (${ping.latencyMs}ms): ${ping.error}`)
              canFlush = false
            }
          } catch (err) {
            console.warn('[auth] ping threw:', err?.message)
            canFlush = false
          }

          if (canFlush) {
            try {
              const flushed = await flushStalePendingWrites(supabase)
              if (flushed) window.dispatchEvent(new CustomEvent('prjct-write-synced'))
            } catch (err) {
              console.warn('[auth] flush failed:', err?.message)
            }
          }

          if (needsDataReload) {
            try {
              await get().loadUserData(currentUser.id)
              // Flash the sync indicator so the user gets a quiet "we caught you up" signal
              window.dispatchEvent(new CustomEvent('prjct-write-synced'))
            } catch (err) { console.warn('[auth] loadUserData failed:', err?.message) }
          }
        })
      },

      _applySession: async (authUser) => {
        // Authenticate immediately from authUser so the shell unblocks while data loads.
        // Profile SELECT and loadUserData run in parallel (previously serial — saved ~150ms
        // per round-trip). The profile UPSERT and post-flush re-fetch are fire-and-forget
        // so they never extend the loading screen.
        const fallbackName = authUser.user_metadata?.name || authUser.email.split('@')[0]
        set({
          currentUser: { id: authUser.id, name: fallbackName, email: authUser.email, avatar: authUser.user_metadata?.avatar_url || null },
          isAuthenticated: true,
          workspace: { name: authUser.user_metadata?.workspace_name || 'My Workspace' },
        })

        const profilePromise = supabase.from('profiles').select('*').eq('id', authUser.id).single()
          .then((res) => res.data)
          .catch((err) => { console.error('_applySession profile error:', err); return null })
        const dataPromise = get().loadUserData(authUser.id)
          .catch((err) => { console.error('_applySession loadUserData error:', err) })

        const [profile] = await Promise.all([profilePromise, dataPromise])

        // Refine currentUser once profile lands
        if (profile) {
          set({
            currentUser: {
              id: authUser.id,
              name: profile.name || fallbackName,
              email: authUser.email,
              avatar: profile.avatar_url || authUser.user_metadata?.avatar_url || null,
            },
            workspace: { name: profile.workspace_name || authUser.user_metadata?.workspace_name || 'My Workspace' },
          })
        }

        // Background tasks — don't block _authReady on these
        ;(async () => {
          // Profile bootstrap upsert. keepOnFail so the profile self-heal lands eventually
          // even if the connection is poor at sign-in.
          retryWrite({
            id: `bootstrap-profile-${authUser.id}`,
            table: 'profiles',
            op: 'upsert',
            keepOnFail: true,
            upsertOptions: { onConflict: 'id' },
            data: {
              id: authUser.id,
              name: profile?.name || fallbackName,
              workspace_name: profile?.workspace_name || authUser.user_metadata?.workspace_name || 'My Workspace',
              updated_at: new Date().toISOString(),
            },
          }, supabase, {
            onFail: (e) => console.warn('[_applySession] profile upsert queued for retry:', e?.message),
          })
          try {
            const flushed = await flushStalePendingWrites(supabase)
            if (flushed) {
              // Realtime will deliver the resolved rows; only re-fetch if realtime is degraded
              const { _realtimeStatus } = get()
              if (_realtimeStatus !== 'connected') {
                try { await get().loadUserData(authUser.id) } catch (e) { console.error('_applySession post-flush reload:', e) }
              }
              window.dispatchEvent(new CustomEvent('prjct-write-synced'))
            }
          } catch (err) {
            console.error('_applySession flush error:', err)
          }
        })()
      },

      loadUserData: async (userId) => {
        if (!supabase) return

        // Fetch member rows + owned projects in parallel for self-healing
        const [memberResult, ownedResult] = await Promise.all([
          supabase.from('project_members').select('project_id, role, permissions').eq('user_id', userId),
          supabase.from('projects').select('id').eq('owner_id', userId),
        ])
        if (memberResult.error) { console.error('loadUserData members:', memberResult.error); return }

        let memberRows = memberResult.data || []
        const memberProjectIdSet = new Set(memberRows.map((r) => r.project_id))
        const missingIds = (ownedResult.data || []).map((r) => r.id).filter((id) => !memberProjectIdSet.has(id))

        // Self-heal: if the user owns projects that have no project_members row, repair them now.
        // CLAUDE.md (#7) — project_members is the gating table for all project data; never lose it.
        // keepOnFail so a transient timeout doesn't drop the repair on the floor.
        if (missingIds.length) {
          console.warn(`[loadUserData] Repairing ${missingIds.length} missing project_members row(s)`)
          const repairResult = await get()._awaitWrite({
            id: `repair-members-${userId}-${Date.now()}`,
            table: 'project_members',
            op: 'upsert',
            keepOnFail: true,
            upsertOptions: { onConflict: 'project_id,user_id' },
            data: missingIds.map((id) => ({ project_id: id, user_id: userId, role: 'owner' })),
          })
          if (!repairResult.ok) {
            console.error('loadUserData repair queued / failed:', repairResult.err?.message || 'see writeQueue')
          } else {
            const { data: repairedRows } = await supabase.from('project_members').select('project_id, role, permissions').eq('user_id', userId)
            memberRows = repairedRows || []
          }
        }

        const projectIds = memberRows.map((r) => r.project_id)
        const roleMap = Object.fromEntries(memberRows.map((r) => [r.project_id, r.role]))
        const permMap = Object.fromEntries(memberRows.map((r) => [r.project_id, r.permissions || {}]))

        let projects = []
        if (projectIds.length) {
          const { data: projectRows, error: projErr } = await supabase
            .from('projects')
            .select('*')
            .in('id', projectIds)
          if (projErr) { console.error('loadUserData projects:', projErr); return }
          // For child boards, workspace ownership trumps the explicit child membership row —
          // so a workspace owner who happens to also have a 'member' row on a child board
          // is still treated as owner there.
          projects = (projectRows || []).map((r) => {
            const explicitRole = roleMap[r.id]
            const parentRole = r.parent_id ? roleMap[r.parent_id] : null
            const role = parentRole === 'owner' ? 'owner' : explicitRole
            return { ...mapProject(r), role, myPermissions: permMap[r.id] }
          })
        }

        // Workspace members automatically see all child client boards
        const workspaceIds = projects.filter((p) => p.isWorkspace).map((p) => p.id)
        if (workspaceIds.length) {
          try {
            const timeout = new Promise((resolve) => setTimeout(() => resolve({ data: null }), 5000))
            const query = supabase.from('projects').select('*').in('parent_id', workspaceIds).eq('completed', false)
            const { data: childRows } = await Promise.race([query, timeout])
            if (childRows?.length) {
              const existingIds = new Set(projects.map((p) => p.id))
              const childProjects = childRows
                .filter((r) => !existingIds.has(r.id))
                // Child boards inherit role + permissions from the parent workspace when there's no explicit row,
                // so a workspace owner is also an owner of every child board (e.g. for completion routing).
                .map((r) => ({
                  ...mapProject(r),
                  role: roleMap[r.id] || roleMap[r.parent_id] || 'member',
                  myPermissions: permMap[r.id] || permMap[r.parent_id] || { create_tasks: true, edit_tasks: true, delete_tasks: true, complete_tasks: true },
                }))
              projects = [...projects, ...childProjects]
            }
          } catch (err) {
            console.warn('[loadUserData] child board fetch failed:', err)
          }
        }

        // Validate persisted activeProjectId
        const currentActive = get().activeProjectId
        const activeProjectId = projects.find((p) => p.id === currentActive) ? currentActive : projects[0]?.id || null

        set({ projects, activeProjectId })

        const allProjectIds = projects.map((p) => p.id)

        if (!allProjectIds.length) {
          // Reconstruct any pending tasks from the write queue before wiping
          // (user may have created a task offline and been removed from all projects before it confirmed)
          const queuedInserts0 = loadQueue().filter((e) => e.table === 'tasks' && e.op === 'insert' && e.data?.id)
          const queuedTasks0 = queuedInserts0.map((e) => mapTask(e.data))
          const queueIds0 = queuedTasks0.map((t) => t.id)
          set((s) => ({
            sections: [],
            tasks: queuedTasks0,
            goals: [],
            _pendingSyncIds: [...new Set([...s._pendingSyncIds, ...queueIds0])],
          }))
          get()._setupRealtime([])
          return
        }

        // Notifications query is isolated so a missing/broken table never blocks the rest of loadUserData
        const [sectionsRes, tasksResOrdered, goalsRes] = await Promise.all([
          supabase.from('sections').select('*').in('project_id', allProjectIds).order('order'),
          supabase.from('tasks').select('*').in('project_id', allProjectIds).order('position'),
          supabase.from('goals').select('*').eq('owner_id', userId),
        ])

        if (sectionsRes.error) {
          console.error('[loadUserData] sections SELECT failed — will not wipe state:', sectionsRes.error)
          return
        }
        // If position column doesn't exist yet (migration not run), fall back to unordered query
        let tasksRes = tasksResOrdered
        if (tasksResOrdered.error) {
          console.warn('[loadUserData] tasks ORDER BY position failed — falling back to unordered (run migration to enable task ordering)', tasksResOrdered.error.message)
          tasksRes = await supabase.from('tasks').select('*').in('project_id', allProjectIds)
        }
        if (tasksRes.error) {
          console.error('[loadUserData] tasks SELECT failed — will not wipe state:', tasksRes.error)
          return
        }

        let notifItems = []
        try {
          const { data: notifRows } = await supabase
            .from('notifications').select('*').eq('user_id', userId)
            .order('created_at', { ascending: false }).limit(50)
          notifItems = (notifRows || []).map((r) => ({
            id: r.id, type: r.type, title: r.title, body: r.body,
            taskId: r.task_id, projectId: r.project_id, read: r.read, createdAt: r.created_at,
          }))
        } catch (_) { /* notifications table may not exist yet */ }

        const sections = (sectionsRes.data || []).map(mapSection)
        const sectionDeliverableMap = Object.fromEntries(
          sections.filter((s) => s.deliverableType).map((s) => [s.id, s.deliverableType])
        )
        let tasks = (tasksRes.data || []).map((r) => {
          const t = mapTask(r)
          if (!t.deliverableType && t.sectionId) t.deliverableType = sectionDeliverableMap[t.sectionId] || null
          return t
        })
        // Preserve tasks that are pending sync (INSERT not yet confirmed by DB).
        // TWO mechanisms — both needed:
        //
        // 1. _pendingSyncIds (in-memory): protects tasks created in THIS session before
        //    any loadUserData call wipes them. Fast, zero-overhead.
        //
        // 2. Write queue reconstruction (localStorage): protects tasks across page reloads.
        //    _pendingSyncIds resets to [] on reload, but the INSERT entry survives in
        //    localStorage. We reconstruct the task directly from the queue's DB row data
        //    so it appears immediately — before flushStalePendingWrites gets a chance to run.
        const { _pendingSyncIds, tasks: currentTasks } = get()
        const dbTaskIds = new Set(tasks.map((t) => t.id))

        // Mechanism 1: in-memory _pendingSyncIds
        if (_pendingSyncIds.length > 0) {
          const pendingTasks = currentTasks.filter((t) => _pendingSyncIds.includes(t.id) && !dbTaskIds.has(t.id))
          if (pendingTasks.length) {
            console.info(`[loadUserData] preserving ${pendingTasks.length} pending task(s) from _pendingSyncIds`)
            tasks = [...tasks, ...pendingTasks]
          }
        }

        // Mechanism 2: queue-based reconstruction (survives page reload)
        // Any INSERT entry in the queue whose ID is not yet in DB → reconstruct the task.
        const preservedIds = new Set(tasks.map((t) => t.id))
        const queuedInserts = loadQueue().filter(
          (e) => e.table === 'tasks' && e.op === 'insert' && e.data?.id && !preservedIds.has(e.data.id)
        )
        const queueReconstructedIds = []
        if (queuedInserts.length) {
          const queuedTasks = queuedInserts.map((e) => mapTask(e.data))
          console.info(`[loadUserData] reconstructing ${queuedTasks.length} task(s) from write queue`)
          tasks = [...tasks, ...queuedTasks]
          queueReconstructedIds.push(...queuedTasks.map((t) => t.id))
        }

        // Mechanism 3: overlay queued UPDATEs and DELETEs (survives page reload).
        // A queued update snapshot is the latest local intent; a queued delete means the
        // user removed the row locally. Without this, an edit or delete made just before a
        // refresh (write not yet flushed) shows the stale server value — the "didn't stick
        // after refresh" / "deleted task came back" bug. The realtime guard keeps these
        // entities protected, so the overlaid values won't be clobbered before they flush.
        {
          const taskQueue = loadQueue().filter((e) => e.table === 'tasks')
          const queuedUpdates = new Map()
          const queuedDeletes = new Set()
          for (const e of taskQueue) {
            if (e.op === 'update' && e.filter?.eq?.id != null) queuedUpdates.set(e.filter.eq.id, e.data)
            else if (e.op === 'delete') {
              if (e.filter?.eq?.id != null) queuedDeletes.add(e.filter.eq.id)
              if (Array.isArray(e.filter?.in?.id)) for (const did of e.filter.in.id) queuedDeletes.add(did)
            }
          }
          if (queuedUpdates.size || queuedDeletes.size) {
            tasks = tasks
              .filter((t) => !queuedDeletes.has(t.id))
              .map((t) => queuedUpdates.has(t.id) ? { ...t, ...mapTaskPatch(queuedUpdates.get(t.id)) } : t)
            console.info(`[loadUserData] overlaid ${queuedUpdates.size} queued update(s), ${queuedDeletes.size} queued delete(s)`)
          }
        }
        // Compute any missing system sections BEFORE the set() call so we can batch everything
        // into a SINGLE set() and avoid back-to-back synchronous useSyncExternalStore notifications.
        const goals = (goalsRes.data || []).map(mapGoal)
        const newSections = []
        projects.forEach((p) => {
          if (p.ownerId !== userId && p.role !== 'owner') return
          const ps = sections.filter((s) => s.projectId === p.id)
          const baseOrder = ps.length + newSections.filter((s) => s.projectId === p.id).length
          if (!ps.some((s) => s.type === 'approval'))
            newSections.push({ id: crypto.randomUUID(), projectId: p.id, name: 'Approval', type: 'approval', order: baseOrder, deliverableType: null, deliverableQuota: null })
          if (!ps.some((s) => s.type === 'complete'))
            newSections.push({ id: crypto.randomUUID(), projectId: p.id, name: 'Complete', type: 'complete', order: baseOrder + 1, deliverableType: null, deliverableQuota: null })
          if (!ps.some((s) => s.type === 'misc' || /^misc$/i.test(s.name)))
            newSections.push({ id: crypto.randomUUID(), projectId: p.id, name: 'Misc', type: 'misc', order: baseOrder + 2, deliverableType: null, deliverableQuota: null })
        })
        console.log(`[loadUserData] loaded ${sections.length} sections, ${tasks.length} tasks for ${allProjectIds.length} projects${newSections.length ? ` (+${newSections.length} auto-sections)` : ''}`)
        // Single set() for all loaded data + any auto-created sections.
        // Also atomically trim _pendingSyncIds: remove only IDs now confirmed in DB
        // (dbTaskIds = raw DB result, so pending tasks not yet in DB keep their IDs protected).
        set((s) => ({
          sections: newSections.length > 0 ? [...sections, ...newSections] : sections,
          tasks,
          goals,
          notifications: notifItems,
          _pendingSyncIds: [...new Set([...s._pendingSyncIds.filter((id) => !dbTaskIds.has(id)), ...queueReconstructedIds])],
          _lastSyncAt: Date.now(),
        }))
        if (newSections.length > 0) {
          newSections.forEach((sec) => {
            try { const st = JSON.parse(localStorage.getItem('prjct-section-types') || '{}'); st[sec.id] = sec.type; localStorage.setItem('prjct-section-types', JSON.stringify(st)) } catch {}
            get()._persistAutoSection(sec)
          })
        }

        // ── Bulk-populate _membersCache for ALL projects so ReportingPage, avatars, etc. work immediately ──
        try {
          const { data: allMemberRows } = await supabase.from('project_members').select('project_id, user_id, role, permissions').in('project_id', allProjectIds)
          if (allMemberRows?.length) {
            const allUserIds = [...new Set(allMemberRows.map((r) => r.user_id))]
            const { data: allProfiles } = await supabase.from('profiles').select('id, name, email, avatar_url').in('id', allUserIds)
            const pMap = {}
            ;(allProfiles || []).forEach((p) => { pMap[p.id] = p })
            const { currentUser } = get()
            const cache = {}
            allMemberRows.forEach((m) => {
              if (!cache[m.project_id]) cache[m.project_id] = []
              const p = pMap[m.user_id]
              cache[m.project_id].push({
                userId: m.user_id,
                role: m.role,
                permissions: m.permissions || { create_tasks: true, edit_tasks: true, delete_tasks: true, manage_sections: true, invite_members: false, approve_tasks: false },
                name: p?.name || (m.user_id === currentUser?.id ? currentUser?.name : null) || 'Unknown',
                email: p?.email || (m.user_id === currentUser?.id ? currentUser?.email : '') || '',
                avatar: p?.avatar_url || (m.user_id === currentUser?.id ? currentUser?.avatar : null) || null,
              })
            })
            // Ensure current user is in every project they belong to
            allProjectIds.forEach((pid) => {
              if (!cache[pid]) cache[pid] = []
              if (currentUser && !cache[pid].some((m) => m.userId === currentUser.id)) {
                const proj = projects.find((pp) => pp.id === pid)
                cache[pid].unshift({ userId: currentUser.id, name: currentUser.name || 'Me', email: currentUser.email || '', avatar: currentUser.avatar || null, role: proj?.ownerId === currentUser.id ? 'owner' : 'member', permissions: { create_tasks: true, edit_tasks: true, delete_tasks: true, manage_sections: true, invite_members: false, approve_tasks: false } })
              }
            })
            set((s) => ({ _membersCache: { ...s._membersCache, ...cache } }))
          }
        } catch (err) {
          console.warn('[loadUserData] bulk members fetch failed:', err)
        }

        get()._setupRealtime(allProjectIds)
      },

      _realtimeChannels: [],
      _realtimeSessionId: null,
      _setupRealtime: (projectIds) => {
        if (!supabase) return
        // Clean up previous channels one by one (don't use removeAllChannels — races with test channel)
        get()._realtimeChannels.forEach((ch) => supabase.removeChannel(ch))
        // Don't set() here — defer to the single set() at the end with final channel list
        if (!projectIds.length) {
          set({ _realtimeChannels: [], _realtimeSessionId: null, _realtimeStatus: 'offline', _realtimeStatusAt: Date.now() })
          return
        }
        set({ _realtimeStatus: 'connecting', _realtimeStatusAt: Date.now() })

        const userId = get().currentUser?.id
        // Unique suffix per setup call — prevents multi-tab channel name collisions
        // (Supabase Realtime will error if two clients use the same channel name on the same socket)
        const sid = crypto.randomUUID().slice(0, 8)
        const channels = []

        // Track CHANNEL_ERROR so we flush the write queue when the connection recovers.
        // _flushing prevents concurrent flushes if multiple channels reconnect at once.
        let _anyChannelHadError = false
        let _flushing = false

        const sub = (name, setup) => {
          const ch = setup(supabase.channel(`${name}-${sid}`))
          ch.subscribe(async (status, err) => {
            if (status === 'SUBSCRIBED') {
              console.log(`[realtime:${name}] ✓ connected`)
              set({ _realtimeStatus: 'connected', _realtimeStatusAt: Date.now() })
              if (_anyChannelHadError && !_flushing) {
                _anyChannelHadError = false
                _flushing = true
                try {
                  console.info('[writeQueue] realtime reconnected — flushing pending writes + refreshing data')
                  const flushed = await flushStalePendingWrites(supabase)
                  const uid = get().currentUser?.id
                  // Always reload on reconnect — other users may have changed data while disconnected.
                  // Realtime does not replay missed events, so a full refresh is the only way to catch up.
                  if (uid) {
                    await get().loadUserData(uid)
                    if (flushed) window.dispatchEvent(new CustomEvent('prjct-write-synced'))
                  }
                } catch (e) {
                  console.warn('[writeQueue] reconnect flush failed:', e)
                } finally {
                  _flushing = false
                }
              }
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              console.warn(`[realtime:${name}] ${status}`, err)
              _anyChannelHadError = true
              set({ _realtimeStatus: 'degraded', _realtimeStatusAt: Date.now() })
            }
          })
          channels.push(ch)
        }

        // Project membership is checked dynamically via get().projects rather than the
        // captured projectIds snapshot — otherwise events for projects the user gains
        // access to after subscription (e.g. workspace-cascade child boards) get dropped.
        const isMyProject = (pid) => get().projects.some((pr) => pr.id === pid)

        // ── Channel 1: Tasks + Sections (2 listeners) ─────────────────────
        sub('rt-tasks', (ch) => ch
          .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, (p) => {
            if (p.eventType === 'INSERT') {
              if (!isMyProject(p.new.project_id)) return
              set((s) => ({ tasks: s.tasks.some((t) => t.id === p.new.id) ? s.tasks : [...s.tasks, mapTask(p.new)] }))
            } else if (p.eventType === 'UPDATE') {
              if (!isMyProject(p.new.project_id)) return
              // Apply an incoming row only when no local write for this task is pending
              // AND the row is same-age-or-newer. This ends the echo-overwrite race: a
              // stale echo of our own earlier write can no longer revert a fresh edit,
              // and it survives write timeouts/retries (the durable queue keeps the
              // task protected, not just the 500ms in-flight window).
              const incoming = mapTask(p.new)
              set((s) => {
                const local = s.tasks.find((t) => t.id === incoming.id)
                if (local && !_shouldApply('tasks', local, incoming)) return s
                return { tasks: s.tasks.map((t) => t.id === incoming.id ? { ...t, ...incoming } : t) }
              })
            } else if (p.eventType === 'DELETE') {
              // Don't remove tasks that are pending local INSERT — the DB row for
              // this ID doesn't exist yet from our perspective, so the DELETE is spurious.
              set((s) => s._pendingSyncIds.includes(p.old.id)
                ? s
                : { tasks: s.tasks.filter((t) => t.id !== p.old.id) }
              )
            }
          })
          .on('postgres_changes', { event: '*', schema: 'public', table: 'sections' }, (p) => {
            if (p.eventType === 'INSERT') {
              if (!isMyProject(p.new.project_id)) return
              set((s) => ({ sections: s.sections.some((sec) => sec.id === p.new.id) ? s.sections : [...s.sections, mapSection(p.new)] }))
            } else if (p.eventType === 'UPDATE') {
              if (!isMyProject(p.new.project_id)) return
              const incomingSec = mapSection(p.new)
              set((s) => {
                const local = s.sections.find((sec) => sec.id === incomingSec.id)
                if (local && !_shouldApply('sections', local, incomingSec)) return s
                return { sections: s.sections.map((sec) => sec.id === incomingSec.id ? { ...sec, ...incomingSec } : sec) }
              })
            } else if (p.eventType === 'DELETE') {
              set((s) => ({ sections: s.sections.filter((sec) => sec.id !== p.old.id) }))
            }
          })
        )

        // ── Channel 2: Projects + Members (2 listeners) ───────────────────
        sub('rt-projects', (ch) => ch
          .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, async (p) => {
            if (p.eventType === 'INSERT') {
              if (p.new.owner_id === userId || projectIds.includes(p.new.id)) await get().loadUserData(userId)
            } else if (p.eventType === 'UPDATE') {
              if (!projectIds.includes(p.new.id)) return
              const incomingProj = mapProject(p.new)
              set((s) => {
                const local = s.projects.find((pr) => pr.id === incomingProj.id)
                if (local && !_shouldApply('projects', local, incomingProj)) return s
                return { projects: s.projects.map((pr) => pr.id === incomingProj.id ? { ...pr, ...incomingProj } : pr) }
              })
            } else if (p.eventType === 'DELETE') {
              set((s) => ({
                projects: s.projects.filter((pr) => pr.id !== p.old.id),
                sections: s.sections.filter((sec) => sec.projectId !== p.old.id),
                tasks: s.tasks.filter((t) => t.projectId !== p.old.id),
              }))
            }
          })
          .on('postgres_changes', { event: '*', schema: 'public', table: 'project_members' }, async (p) => {
            if (p.eventType === 'INSERT') {
              if (p.new.user_id === userId) { await get().loadUserData(userId) }
              else if (projectIds.includes(p.new.project_id)) { await get().getProjectMembers(p.new.project_id, { force: true }) }
            } else if (p.eventType === 'UPDATE') {
              // When the current user's own membership row changes, patch projects[] in-place
              // so role/permission-gated logic reads fresh data without a reload.
              // Workspace updates cascade to child boards via inheritance — re-run loadUserData to recompute.
              if (p.new.user_id === userId && projectIds.includes(p.new.project_id)) {
                const updated = get().projects.find((pr) => pr.id === p.new.project_id)
                if (updated?.isWorkspace) {
                  await get().loadUserData(userId)
                } else {
                  set((s) => ({
                    projects: s.projects.map((pr) =>
                      pr.id === p.new.project_id
                        ? { ...pr, role: p.new.role, myPermissions: p.new.permissions || pr.myPermissions }
                        : pr
                    ),
                  }))
                }
              }
              if (projectIds.includes(p.new.project_id)) { await get().getProjectMembers(p.new.project_id, { force: true }) }
            } else if (p.eventType === 'DELETE') {
              if (p.old.user_id === userId) {
                const pid = p.old.project_id
                const wasSelf = _selfLeftProjects.has(pid)
                _selfLeftProjects.delete(pid)
                get().invalidateMembersCache(pid)
                // Capture project name BEFORE removing from state
                const projectName = get().projects.find((pr) => pr.id === pid)?.name || 'a project'
                set((s) => ({
                  projects: s.projects.filter((pr) => pr.id !== pid),
                  sections: s.sections.filter((sec) => sec.projectId !== pid),
                  tasks: s.tasks.filter((t) => t.projectId !== pid),
                  activeProjectId: s.activeProjectId === pid ? null : s.activeProjectId,
                  // Only show kicked screen if someone else removed us
                  kickedFromProject: wasSelf ? s.kickedFromProject : { id: pid, name: projectName },
                }))
              } else if (projectIds.includes(p.old.project_id)) { await get().getProjectMembers(p.old.project_id, { force: true }) }
            }
          })
        )

        // ── Channel 3: Profiles + Goals (2 listeners) ─────────────────────
        sub('rt-user', (ch) => ch
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles' }, (p) => {
            if (p.new.id === userId) {
              set((s) => ({
                currentUser: { ...s.currentUser, name: p.new.name || s.currentUser.name, avatar: p.new.avatar_url || s.currentUser?.avatar || null },
                workspace: p.new.workspace_name ? { ...s.workspace, name: p.new.workspace_name } : s.workspace,
              }))
            }
            // Update member cache inline with new profile data instead of just invalidating
            const updatedId = p.new.id
            const newName = p.new.name
            const newAvatar = p.new.avatar_url
            const newEmail = p.new.email
            set((s) => {
              const cache = { ...s._membersCache }
              let changed = false
              for (const pid of Object.keys(cache)) {
                const idx = cache[pid].findIndex((m) => m.userId === updatedId)
                if (idx !== -1) {
                  cache[pid] = [...cache[pid]]
                  cache[pid][idx] = { ...cache[pid][idx], name: newName || cache[pid][idx].name, avatar: newAvatar || cache[pid][idx].avatar, email: newEmail || cache[pid][idx].email }
                  changed = true
                }
              }
              return changed ? { _membersCache: cache } : {}
            })
          })
          .on('postgres_changes', { event: '*', schema: 'public', table: 'goals' }, (p) => {
            if (p.eventType === 'INSERT') {
              if (p.new.owner_id !== userId) return
              set((s) => ({ goals: s.goals.some((g) => g.id === p.new.id) ? s.goals : [...s.goals, mapGoal(p.new)] }))
            } else if (p.eventType === 'UPDATE') {
              const incomingGoal = mapGoal(p.new)
              set((s) => {
                const local = s.goals.find((g) => g.id === incomingGoal.id)
                if (local && !_shouldApply('goals', local, incomingGoal)) return s
                return { goals: s.goals.map((g) => g.id === incomingGoal.id ? { ...g, ...incomingGoal } : g) }
              })
            } else if (p.eventType === 'DELETE') {
              set((s) => ({ goals: s.goals.filter((g) => g.id !== p.old.id) }))
            }
          })
        )

        // ── Channel 4: Notifications (1 listener) ────────────────────────
        sub('rt-notif', (ch) => ch
          .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, (p) => {
            if (p.eventType === 'INSERT' && p.new.user_id === userId) {
              if (get().notifications.some((notif) => notif.id === p.new.id)) return
              get().addNotification({ id: p.new.id, type: p.new.type, title: p.new.title, body: p.new.body, taskId: p.new.task_id, projectId: p.new.project_id, read: false, createdAt: p.new.created_at })
            } else if (p.eventType === 'UPDATE' && p.new.user_id === userId) {
              set((s) => ({ notifications: s.notifications.map((n) => n.id === p.new.id ? { ...n, read: p.new.read } : n) }))
            }
          })
        )

        // Single set() for all realtime state — avoids multiple synchronous React commits
        set({ _realtimeChannels: channels, _realtimeSessionId: sid })
      },

      loginWithEmail: async (email, password) => {
        if (!supabase) {
          set({ currentUser: { id: 'u1', name: email.split('@')[0], email, avatar: null }, isAuthenticated: true, workspace: MOCK_WS, projects: MOCK_PROJECTS, sections: MOCK_SECTIONS, tasks: mockTasks('u1'), goals: [], activeProjectId: 'p1' })
          return { error: null }
        }
        const { data, error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) return { error }
        await get()._applySession(data.user)
        return { error: null }
      },

      signUpWithEmail: async (email, password, name, workspaceName) => {
        if (!supabase) {
          const user = { id: 'u' + Date.now(), name: name || email.split('@')[0], email, avatar: null }
          set({ currentUser: user, isAuthenticated: true, workspace: { name: workspaceName || 'My Workspace' }, projects: [], sections: [], tasks: [], goals: [], activeProjectId: null })
          return { error: null }
        }
        const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { name, workspace_name: workspaceName } } })
        if (error) return { error }
        if (!data.user) return { error: { message: 'Check your email to confirm your account.' } }
        // Profile upsert is critical for sign-up — without it the user can't see their own data.
        // keepOnFail so a transient timeout doesn't strand the new user without a profile row.
        await get()._awaitWrite({
          id: `signup-profile-${data.user.id}`,
          table: 'profiles',
          op: 'upsert',
          keepOnFail: true,
          upsertOptions: { onConflict: 'id' },
          data: { id: data.user.id, name, email, workspace_name: workspaceName || 'My Workspace' },
        })
        await get()._applySession(data.user)
        return { error: null }
      },

      // Legacy mock bypass
      login: (user) => set({ currentUser: user, isAuthenticated: true, workspace: MOCK_WS, projects: MOCK_PROJECTS, sections: MOCK_SECTIONS, tasks: mockTasks(user.id), goals: [], activeProjectId: 'p1' }),

      logout: async () => {
        if (supabase) { supabase.removeAllChannels(); await supabase.auth.signOut() }
        set({ isAuthenticated: false, currentUser: null, projects: [], sections: [], tasks: [], goals: [], workspace: null, activeProjectId: null })
      },

      updateProfile: async (updates) => {
        set((s) => ({
          currentUser: { ...s.currentUser, ...updates },
          workspace: updates.workspaceName ? { ...s.workspace, name: updates.workspaceName } : s.workspace,
        }))
        if (!supabase) return
        const user = get().currentUser
        if (!user?.id) return
        const patch = {}
        if (updates.name !== undefined) patch.name = updates.name
        if (updates.email !== undefined) patch.email = updates.email
        if (updates.avatar !== undefined) patch.avatar_url = updates.avatar
        if (updates.workspaceName !== undefined) patch.workspace_name = updates.workspaceName
        if (Object.keys(patch).length) {
          get()._writeStart()
          retryWrite({
            id: `update-profile-${user.id}-${Date.now()}`,
            table: 'profiles',
            op: 'update',
            filter: { eq: { id: user.id } },
            data: { ...patch, updated_at: new Date().toISOString() },
          }, supabase, {
            onOk: () => get()._writeOk(),
            onBlocked: () => get()._writeFail('Profile update blocked — permission denied.', true, { table: 'profiles', op: 'update', entityId: user.id }),
            onFail: (e, { permanent } = {}) => get()._writeFail(
              permanent ? 'Failed to save profile changes.' : 'Connection issue — profile changes saved locally and will sync when connection is restored.',
              permanent,
              { table: 'profiles', op: 'update', entityId: user.id, code: e?.code }
            ),
          })
        }
      },

      updateWorkspace: (updates) => set((s) => ({ workspace: { ...s.workspace, ...updates } })),

      sendPasswordReset: async (email) => {
        if (!supabase) return { error: null }
        return supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` })
      },

      changePassword: async (newPassword) => {
        if (!supabase) return { error: null }
        return supabase.auth.updateUser({ password: newPassword })
      },

      deleteAttachmentFromStorage: async (url) => {
        if (!url) return
        try {
          // R2-hosted file → server-side delete (credentials live in the function).
          const r2Key = r2KeyFromUrl(url)
          if (r2Key) {
            const token = await get()._resolveAccessToken()
            if (token) await r2Presign({ key: r2Key, op: 'delete', token }).catch((e) => console.error('[deleteAttachmentFromStorage:r2]', e?.message))
            return
          }
          // Legacy Supabase Storage URL (pre-migration files) → existing path.
          if (!supabase) return
          const match = url.match(/\/storage\/v1\/object\/public\/attachments\/(.+)/)
          if (match?.[1]) {
            await supabase.storage.from('attachments').remove([decodeURIComponent(match[1])])
          }
        } catch (e) {
          console.error('[deleteAttachmentFromStorage]', e)
        }
      },

      // Shared XHR uploader. Bypasses the supabase-js storage client (which uses
      // TUS resumable uploads for files >6 MB — TUS can silently hang). Used by
      // uploadAttachment, uploadAttachmentThumb, and the IG planner upload path.
      //
      // Token handling: reuses the access token cached by onAuthStateChange to
      // avoid supabase.auth.getSession() on the hot path — getSession holds
      // Supabase's internal Web Locks lock and can hang for seconds on Safari
      // (autoRefreshToken throttled in backgrounded tabs, lock contention across
      // concurrent uploads). Only force-refreshes on 401.
      // Resolve a valid Supabase access token, reusing the one cached by
      // onAuthStateChange (avoids getSession's Web Locks contention on the hot path);
      // force-refreshes on demand (e.g. after a 401).
      _resolveAccessToken: async ({ forceRefresh = false } = {}) => {
        if (!forceRefresh) {
          const cached = get()._authToken
          const expiresAt = get()._authExpiresAt
          if (cached && expiresAt - Date.now() > 30_000) return cached
        }
        if (!supabase) return null
        try {
          const refreshed = await Promise.race([
            supabase.auth.refreshSession(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('refreshSession timed out')), 8000)),
          ])
          const newToken = refreshed.data?.session?.access_token || null
          if (newToken) set({ _authToken: newToken, _authExpiresAt: (refreshed.data.session.expires_at || 0) * 1000 })
          return newToken
        } catch {
          return null
        }
      },

      // Upload an attachment blob directly to Cloudflare R2 via a presigned PUT URL.
      // Same key convention as the Supabase path, so display + migration stay uniform.
      _uploadToR2: async ({ blob, key, contentType, onProgress, timeoutMs = 600000 }) => {
        const put = (signedUrl) => new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('PUT', signedUrl)
          xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream')
          if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total) }
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) { resolve(); return }
            reject(new Error(`R2 upload failed (${xhr.status}): ${(xhr.responseText || '').slice(0, 200)}`))
          }
          xhr.onerror = () => reject(new Error('Network error during upload'))
          xhr.ontimeout = () => reject(new Error('Upload timed out'))
          xhr.timeout = timeoutMs
          xhr.send(blob)
        })
        _activeUploads++
        try {
          let token = await get()._resolveAccessToken()
          if (!token) return { url: null, error: 'Session unavailable — try refreshing the page' }
          let signed
          try {
            ({ url: signed } = await r2Presign({ key, contentType, op: 'put', token }))
          } catch (e) {
            if (e?.status === 401) {
              token = await get()._resolveAccessToken({ forceRefresh: true })
              if (!token) return { url: null, error: 'Session unavailable — try refreshing the page' }
              ;({ url: signed } = await r2Presign({ key, contentType, op: 'put', token }))
            } else throw e
          }
          await put(signed)
          return { url: r2PublicUrl(key), error: null }
        } catch (err) {
          console.error('[_uploadToR2] threw:', err?.message, err)
          return { url: null, error: err?.message || 'Upload failed' }
        } finally {
          _activeUploads = Math.max(0, _activeUploads - 1)
        }
      },

      _uploadToStorage: async ({ blob, bucket, path, contentType, onProgress, timeoutMs = 600000 }) => {
        // Attachments go to Cloudflare R2 when configured (zero-egress, escapes the
        // Supabase Storage limit). avatars / ig-images stay on Supabase. When R2 isn't
        // configured yet, fall through to Supabase so the app keeps working.
        if (bucket === 'attachments' && r2Enabled()) {
          return get()._uploadToR2({ blob, key: path, contentType, onProgress, timeoutMs })
        }
        if (!supabase) return { url: null, error: 'Not connected to Supabase' }
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const uploadUrl = `${supabaseUrl}/storage/v1/object/${bucket}/${path}`

        const sendUpload = (authToken) => new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', uploadUrl)
          xhr.setRequestHeader('Authorization', `Bearer ${authToken}`)
          xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream')
          xhr.setRequestHeader('x-upsert', 'false')
          if (onProgress) {
            xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total) }
          }
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) { resolve(xhr.responseText); return }
            if (xhr.status === 401) { reject(Object.assign(new Error('Unauthorized'), { status: 401 })); return }
            // Supabase Storage returns JSON like {"statusCode":"400","error":"Payload too large","message":"..."}.
            // statusText is often empty so the bare status code is useless on its own — pull the real reason
            // out of the body. Fall back to status code if the body isn't JSON.
            let detail = ''
            try {
              const body = JSON.parse(xhr.responseText)
              detail = body?.message || body?.error || ''
            } catch {
              detail = (xhr.responseText || '').slice(0, 200)
            }
            reject(new Error(detail ? `Upload failed (${xhr.status}): ${detail}` : `Upload failed: ${xhr.status}`))
          }
          xhr.onerror = () => reject(new Error('Network error during upload'))
          xhr.ontimeout = () => reject(new Error('Upload timed out'))
          xhr.timeout = timeoutMs
          xhr.send(blob)
        })

        _activeUploads++
        try {
          let token = await get()._resolveAccessToken()
          if (!token) return { url: null, error: 'Session unavailable — try refreshing the page' }
          try {
            await sendUpload(token)
          } catch (err) {
            if (err?.status === 401) {
              token = await get()._resolveAccessToken({ forceRefresh: true })
              if (!token) return { url: null, error: 'Session unavailable — try refreshing the page' }
              await sendUpload(token)
            } else {
              throw err
            }
          }
          const { data } = supabase.storage.from(bucket).getPublicUrl(path)
          return { url: data?.publicUrl || null, error: null }
        } catch (err) {
          console.error(`[_uploadToStorage:${bucket}] threw:`, err?.message, err)
          return { url: null, error: err?.message || 'Upload failed' }
        } finally {
          _activeUploads = Math.max(0, _activeUploads - 1)
        }
      },

      uploadAttachment: async (file, taskId, onProgress) => {
        if (!supabase) return null
        // Bucket file_size_limit is 100 MB (see supabase-setup.sql). Server returns an
        // opaque 400 with empty statusText when this is exceeded — fail fast client-side
        // so the user gets a real "this file is too big" message instead of "Upload failed: 400".
        const ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024
        if (file.size > ATTACHMENT_MAX_BYTES) {
          const mb = (file.size / 1024 / 1024).toFixed(1)
          return { url: null, error: `File is ${mb} MB — attachments must be under 100 MB.` }
        }
        const userId = get().currentUser?.id || 'anon'
        const ext = file.name.split('.').pop()
        const path = `${userId}/${taskId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
        return get()._uploadToStorage({
          blob: file,
          bucket: 'attachments',
          path,
          contentType: file.type || 'application/octet-stream',
          onProgress,
          timeoutMs: 600000, // 10 min — supports large video uploads
        })
      },

      // Companion to uploadAttachment: stores a small JPEG next to the original
      // so list/card views can render a thumb instead of fetching the full file.
      // Path: attachments/{userId}/{taskId}/thumbs/{attachmentId}.jpg
      uploadAttachmentThumb: async (blob, taskId, attachmentId) => {
        if (!supabase) return { url: null, error: 'Not connected to Supabase' }
        const userId = get().currentUser?.id || 'anon'
        const path = `${userId}/${taskId}/thumbs/${attachmentId}.jpg`
        return get()._uploadToStorage({
          blob,
          bucket: 'attachments',
          path,
          contentType: 'image/jpeg',
          timeoutMs: 60000,
        })
      },

      // Lazy backfill for legacy image attachments that lack a thumbUrl. Pulls
      // the original once (the trade — but afterwards the CDN-cached thumb
      // serves every subsequent render). Guarded against thundering herd via
      // in-memory Set + sessionStorage cap. On permanent failure marks the
      // attachment with thumbBackfillFailed so we never re-try the same one.
      // Videos are deliberately not backfilled — generating a thumb from a
      // remote video requires fetching the file, which is the exact thing
      // this whole project is trying to stop.
      backfillAttachmentThumb: async (taskId, attachmentId) => {
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
        if (localStorage.getItem('prjct-thumb-backfill-disabled') === '1') return
        const key = `${taskId}::${attachmentId}`
        if (_thumbBackfillInFlight.has(key)) return
        const SESS_KEY = 'prjct-thumb-backfill-count'
        const sessionCount = Number(sessionStorage.getItem(SESS_KEY) || '0')
        if (sessionCount >= 20) return

        const task = get().tasks.find((t) => t.id === taskId)
        const att = task?.attachments?.find((a) => a.id === attachmentId)
        if (!att || !att.isImage || att.thumbUrl || att.thumbBackfillFailed || att.uploading) return
        if (!att.url) return

        _thumbBackfillInFlight.add(key)
        sessionStorage.setItem(SESS_KEY, String(sessionCount + 1))

        const markFailed = () => {
          const cur = get().tasks.find((t) => t.id === taskId)
          if (!cur?.attachments) return
          get().updateTask(taskId, {
            attachments: cur.attachments.map((a) =>
              a.id === attachmentId ? { ...a, thumbBackfillFailed: true } : a
            ),
          })
        }

        try {
          const blob = await generateImageThumbFromUrl(att.url)
          if (!blob) {
            markFailed()
            return
          }
          const { url, error } = await get().uploadAttachmentThumb(blob, taskId, attachmentId)
          if (!url) {
            console.warn('[backfillAttachmentThumb] upload failed:', error)
            markFailed()
            return
          }
          const cur = get().tasks.find((t) => t.id === taskId)
          if (!cur?.attachments) return
          get().updateTask(taskId, {
            attachments: cur.attachments.map((a) =>
              a.id === attachmentId ? { ...a, thumbUrl: url } : a
            ),
          })
        } catch (e) {
          console.warn('[backfillAttachmentThumb] threw:', e?.message)
          markFailed()
        } finally {
          _thumbBackfillInFlight.delete(key)
        }
      },

      uploadAvatar: async (file) => {
        if (!supabase) return { error: { message: 'Supabase not configured' } }
        const user = get().currentUser
        if (!user?.id) return { error: { message: 'Not logged in' } }
        if (file.size > 5 * 1024 * 1024) return { error: { message: 'Image must be under 5 MB' } }
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif']
        if (!allowedTypes.includes(file.type)) return { error: { message: 'Only JPEG, PNG, GIF, WebP, or AVIF images are allowed' } }
        const ext = file?.name?.split('.').pop() || 'jpg'
        const path = `${user.id}/avatar.${ext}`
        try {
          const uploadPromise = supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type || 'image/jpeg' })
          const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Upload timed out after 15s')), 15000))
          const { error: uploadErr } = await Promise.race([uploadPromise, timeout])
          if (uploadErr) return { error: uploadErr }
        } catch (err) {
          console.error('uploadAvatar:', err)
          return { error: { message: err.message || 'Upload failed' } }
        }
        const { data } = supabase.storage.from('avatars').getPublicUrl(path)
        const url = `${data.publicUrl}?t=${Date.now()}`
        // Update local state immediately
        set((s) => ({ currentUser: { ...s.currentUser, avatar: url } }))
        // Save to auth user metadata (always allowed, no RLS issues)
        supabase.auth.updateUser({ data: { avatar_url: url } }).catch(() => {})
        // Save to profiles table — tracked so sync indicator reflects the write
        get()._writeStart()
        retryWrite({
          id: `upload-avatar-${user.id}-${Date.now()}`,
          table: 'profiles',
          op: 'update',
          filter: { eq: { id: user.id } },
          data: { avatar_url: url, updated_at: new Date().toISOString() },
        }, supabase, {
          onOk: () => get()._writeOk(),
          onBlocked: () => get()._writeFail('Avatar saved but profile update blocked — permission denied.', true, { table: 'profiles', op: 'update', entityId: user.id }),
          onFail: (e, { permanent } = {}) => get()._writeFail(
            permanent ? 'Avatar saved but profile not updated.' : 'Avatar saved — profile update will sync when connection is restored.',
            permanent,
            { table: 'profiles', op: 'update', entityId: user.id, code: e?.code }
          ),
        })
        // Update avatar inline in member cache (never wipe — causes "Unknown" members)
        set((s) => {
          const cache = { ...s._membersCache }
          let changed = false
          for (const pid of Object.keys(cache)) {
            const idx = cache[pid].findIndex((m) => m.userId === user.id)
            if (idx !== -1) {
              cache[pid] = [...cache[pid]]
              cache[pid][idx] = { ...cache[pid][idx], avatar: url }
              changed = true
            }
          }
          return changed ? { _membersCache: cache } : {}
        })
        return { url }
      },
    }),
    {
      name: 'prjct-ui-prefs',
      // Only persist UI preferences — all data lives in Supabase now
      partialize: (s) => ({
        theme: s.theme,
        accentColor: s.accentColor,
        sidebarCollapsed: s.sidebarCollapsed,
        activeProjectId: s.activeProjectId,
        notifications: s.notifications,
        notifPrefs: s.notifPrefs,
      }),
    }
  )
)
